import type { AppLanguage } from './features/app/language-policy';

export {
  getBrowserLanguages,
  getInitialAppLanguage,
  getLocaleForLanguage,
  resolveLanguage,
  type AppLanguage,
} from './features/app/language-policy';

export type TranslationVariables = Record<string, number | string>;

export type Translator = {
  (message: string, variables?: TranslationVariables): string;
  readonly language?: AppLanguage;
};

const translations: Record<AppLanguage, Record<string, string>> = {
  en: {},
  pl: {
    'Different targets per set': 'Różne cele dla poszczególnych serii',
    Saved: 'Zapisano',
    'Offline. Saved on this device.': 'Offline. Zapisano na tym urządzeniu.',
    'Sync failed. Saved on this device.':
      'Nie udało się zsynchronizować. Zapisano na tym urządzeniu.',
    'Saved on this device.': 'Zapisano na tym urządzeniu.',
    'Adjust rest': 'Zmień przerwę',
    'Add set': 'Dodaj serię',
    Sets: 'Serie',
    Weight: 'Obciążenie',
    Apply: 'Zastosuj',
    'Could not save on this device. Retry to continue.':
      'Nie udało się zapisać na tym urządzeniu. Spróbuj ponownie, aby kontynuować.',
    'Current set': 'Bieżąca seria',
    'Rest (sec)': 'Przerwa (s)',
    'Hold (sec)': 'Utrzymanie (s)',
    'Time (sec)': 'Czas (s)',
    'Remove upcoming set': 'Usuń przyszłą serię',
    'Could not save on this device. Try Apply again.':
      'Nie udało się zapisać na tym urządzeniu. Spróbuj ponownie zastosować zmiany.',
    'Enter valid targets before applying.':
      'Wpisz poprawne wartości przed zastosowaniem zmian.',
    'Enter exact values': 'Wpisz dokładne wartości',
    'No weight': 'Bez obciążenia',
    'Adjust set': 'Zmień serię',
    'Open in Instagram': 'Otwórz w Instagramie',
    'Checking for plan updates...': 'Sprawdzanie aktualizacji planu...',
    'Could not check for plan updates.':
      'Nie udało się sprawdzić aktualizacji planu.',
    'A newer version of this saved plan is available.':
      'Dostępna jest nowsza wersja tego zapisanego planu.',
    'Review update': 'Sprawdź zmiany',
    'Update saved plan': 'Zaktualizuj zapisany plan',
    'Review the current and new plan before updating. Your edits will be replaced. Past workouts stay in history, current progress starts fresh, and reminders stay linked.':
      'Porównaj obecną i nową wersję planu. Twoje zmiany zostaną zastąpione. Dawne treningi pozostaną w historii, bieżący postęp zacznie się od nowa, a powiadomienia pozostaną przypisane.',
    'Current saved plan': 'Obecny zapisany plan',
    'New version': 'Nowa wersja',
    'Demo available': 'Demonstracja dostępna',
    'No demo': 'Bez demonstracji',
    '{count} sets': '{count} serii',
    '{count} s hold': '{count} s utrzymania',
    'The plan could not be updated. Review the latest changes and try again.':
      'Nie udało się zaktualizować planu. Sprawdź najnowsze zmiany i spróbuj ponownie.',
    'Keep current plan': 'Zachowaj obecny plan',
    'Updating plan...': 'Aktualizowanie planu...',
    'Apply plan update': 'Zastosuj aktualizację planu',
    'Saved plan updated.': 'Zapisany plan został zaktualizowany.',
    'Create routine with AI': 'Utwórz plan z AI',
    'Create with AI': 'Utwórz z AI',
    'Turn existing workout notes into an editable routine draft.':
      'Zamień istniejące notatki treningowe w edytowalny szkic planu.',
    'Paste your workout notes or describe the routine you want to create.':
      'Wklej notatki treningowe lub opisz plan, który chcesz utworzyć.',
    'Review every exercise and prescription before creating the routine.':
      'Sprawdź każde ćwiczenie i zalecenia przed utworzeniem planu.',
    'Review routine draft': 'Sprawdź szkic planu',
    'Confirm the name and routine outline. You can edit everything in the next step.':
      'Potwierdź nazwę i zarys planu. Wszystko możesz edytować w następnym kroku.',
    'Confirm the name and routine outline. You can edit it after creating.':
      'Potwierdź nazwę i zarys planu. Możesz go edytować po utworzeniu.',
    'Routine text': 'Treść planu',
    'Example: Full-body strength. Barbell Squat: 4 sets of 6 reps, 3-second eccentric and 1-second concentric, rest 90 seconds. Barbell Bent Over Row: 3 sets of 8 reps, rest 60 seconds. Elbow Side Plank: 3 x 30 seconds on each side, rest 45 seconds.':
      'Przykład: Siła całego ciała. Przysiad ze sztangą na plecach: 4 serie po 6 powtórzeń, 3 sekundy fazy ekscentrycznej i 1 sekunda fazy koncentrycznej, przerwa 90 sekund. Wiosłowanie sztangą w opadzie: 3 serie po 8 powtórzeń, przerwa 60 sekund. Deska bokiem na przedramieniu: 3 × 30 sekund na każdą stronę, przerwa 45 sekund.',
    'At least 3 letters or numbers.': 'Co najmniej 3 litery lub cyfry.',
    '{count} of {limit} included generations remaining':
      'Pozostało {count} z {limit} generowań w pakiecie',
    'This is your last included generation this week.':
      'To ostatnie generowanie w Twoim tygodniowym pakiecie.',
    'Need more later? You can add 10 generations after using your weekly allowance.':
      'Potrzebujesz więcej? Po wykorzystaniu tygodniowego pakietu możesz dokupić 10 generowań.',
    'Forma provides general exercise guidance and is not a medical service.':
      'Forma oferuje ogólne wskazówki dotyczące ćwiczeń i nie jest usługą medyczną.',
    'Generate draft': 'Wygeneruj szkic',
    'Change prompt': 'Zmień opis',
    'Back to description': 'Wróć do opisu',
    'Create and edit routine': 'Utwórz i edytuj plan',
    'Create and review in editor': 'Utwórz i sprawdź w edytorze',
    'Create and continue to editor': 'Utwórz i przejdź do edytora',
    'Draft ready for review': 'Szkic gotowy do sprawdzenia',
    'Routine draft': 'Szkic planu',
    'You can change the generated name before creating.':
      'Możesz zmienić wygenerowaną nazwę przed utworzeniem planu.',
    'Routine name must be 80 characters or fewer.':
      'Nazwa planu może mieć maksymalnie 80 znaków.',
    'Routine notes': 'Notatki do planu',
    'Forma matches': 'Dopasowania Forma',
    'Need review': 'Do sprawdzenia',
    'Review in editor': 'Sprawdź w edytorze',
    'Finish in the routine editor': 'Dokończ w edytorze planu',
    'Edit the routine after creating': 'Edytuj plan po utworzeniu',
    'Routine outline': 'Zarys planu',
    'Editable later': 'Edytujesz później',
    'Add prescription in editor': 'Dodaj zalecenia w edytorze',
    'Exercise review': 'Sprawdzenie ćwiczeń',
    'Items marked in amber need your attention.':
      'Elementy oznaczone kolorem bursztynowym wymagają uwagi.',
    'Forma exercise': 'Ćwiczenie Forma',
    'Custom draft': 'Własny szkic',
    Prescription: 'Zalecenia',
    'Sets: {value}': 'Serie: {value}',
    'Reps: {value}': 'Powtórzenia: {value}',
    'Hold: {value} s': 'Trzymanie: {value} s',
    'Different hold times were specified for the sides: {first} s and {second} s. Choose one value or split the exercise in the editor.':
      'Podano różne czasy trzymania dla stron: {first} s i {second} s. Wybierz jedną wartość albo rozdziel ćwiczenie w edytorze.',
    'Rest: {value} s': 'Przerwa: {value} s',
    'Lowering: {value} s': 'Opuszczanie: {value} s',
    'Lifting: {value} s': 'Podnoszenie: {value} s',
    'Check its name and details in the editor before sharing.':
      'Sprawdź nazwę i szczegóły w edytorze przed udostępnieniem.',
    'What happens next?': 'Co stanie się dalej?',
    'Forma creates a private routine and opens it in the editor. Review it before sharing.':
      'Forma utworzy prywatny plan i otworzy go w edytorze. Sprawdź go przed udostępnieniem.',
    'Change exercise names, prescriptions, and details in the editor before sharing.':
      'Nazwy ćwiczeń, zalecenia i szczegóły zmienisz w edytorze przed udostępnieniem.',
    'You can change exercise names, prescriptions, and details after creating the routine.':
      'Nazwy ćwiczeń, zalecenia i szczegóły możesz zmienić po utworzeniu planu.',
    'Forma creates a private routine and opens its details. Review it before starting.':
      'Forma utworzy prywatny plan i otworzy jego szczegóły. Sprawdź go przed rozpoczęciem.',
    'Matched Forma exercise': 'Dopasowane ćwiczenie Forma',
    'Custom exercise needs review': 'Ćwiczenie własne wymaga sprawdzenia',
    'Prescription not provided': 'Nie podano zaleceń',
    'No prescription was present in the source.':
      'W źródle nie podano zaleceń.',
    'No Forma exercise matched; review this custom draft.':
      'Nie znaleziono ćwiczenia Forma; sprawdź ten własny szkic.',
    'The closest match was uncertain, so this remains a custom draft.':
      'Najbliższe dopasowanie było niepewne, więc pozostawiono własny szkic.',
    'Add 10 generations': 'Dodaj 10 generowań',
    '{price} for 10 additional generations.':
      '{price} za 10 dodatkowych generowań.',
    'Billing is unavailable for your current currency.':
      'Płatność nie jest dostępna dla Twojej obecnej waluty.',
    'Credits do not expire, but an active paid Forma plan is required to use them.':
      'Środki nie wygasają, ale do ich użycia potrzebny jest aktywny płatny abonament Forma.',
    'Or wait until {date} for an included generation.':
      'Możesz też poczekać do {date} na generowanie w pakiecie.',
    'Payment processing. This screen will update when Stripe confirms your credits.':
      'Płatność jest przetwarzana. Ten ekran zaktualizuje się, gdy Stripe potwierdzi środki.',
    'Stripe is taking longer than expected. Your balance is unchanged until payment is confirmed.':
      'Stripe potrzebuje więcej czasu. Twoje saldo nie zmieni się, dopóki płatność nie zostanie potwierdzona.',
    'Check payment status': 'Sprawdź status płatności',
    'Payment was not completed. Your balance was not changed, and you can try again.':
      'Płatność nie została ukończona. Saldo nie uległo zmianie i możesz spróbować ponownie.',
    'Payment confirmed. Your AI generation balance is up to date.':
      'Płatność została potwierdzona. Twoje saldo generowań AI jest aktualne.',
    'Checkout was cancelled. Your prompt is still here.':
      'Płatność została anulowana. Twój opis nadal tu jest.',
    '{count} purchased generations available.':
      'Dostępne generowania z dokupionego pakietu: {count}.',
    'The generated routine could not be created.':
      'Nie udało się utworzyć wygenerowanego planu.',
    'AI routine generation is unavailable right now.':
      'Generowanie planów z AI jest teraz niedostępne.',
    'Could not load your AI generation allowance. Please try again.':
      'Nie udało się pobrać limitu generowań AI. Spróbuj ponownie.',
    'We could not generate a routine right now. Please try again.':
      'Nie udało się teraz wygenerować planu. Spróbuj ponownie.',
    'We could not start checkout right now. Please try again.':
      'Nie udało się rozpocząć płatności. Spróbuj ponownie.',
    'Sign in to use AI routine generation.':
      'Zaloguj się, aby korzystać z generowania planów AI.',
    'An active paid Forma plan is required to use AI routine generation.':
      'Do generowania planów AI potrzebny jest aktywny płatny abonament Forma.',
    'Your AI generation allowance is exhausted.':
      'Twój limit generowań AI został wykorzystany.',
    'Too many generation attempts. Try again in a few minutes.':
      'Zbyt wiele prób generowania. Spróbuj ponownie za kilka minut.',
    'A payment session is already being prepared. Please wait a moment.':
      'Sesja płatności jest już przygotowywana. Poczekaj chwilę.',
    'This generated draft is no longer available. Generate a new one.':
      'Ten wygenerowany szkic nie jest już dostępny. Wygeneruj nowy.',
    'This generated draft has expired. Generate a new one.':
      'Ten wygenerowany szkic wygasł. Wygeneruj nowy.',
    'Check the routine description and try again.':
      'Sprawdź opis planu i spróbuj ponownie.',
    'AI routine created. Review it before sharing.':
      'Plan AI został utworzony. Sprawdź go przed udostępnieniem.',
    'AI routine created. Review it before starting.':
      'Plan AI został utworzony. Sprawdź go przed rozpoczęciem.',
    'Find or create with AI': 'Znajdź lub utwórz z AI',
    'Describe what you need. Forma searches existing routines before generating one.':
      'Opisz, czego potrzebujesz. Forma najpierw przeszuka istniejące plany.',
    'Review custom routine': 'Sprawdź własny plan',
    'Find the right routine': 'Znajdź odpowiedni plan',
    'Describe what you need. Searching Forma is free and comes before AI generation.':
      'Opisz, czego potrzebujesz. Wyszukiwanie w Formie jest bezpłatne i poprzedza generowanie AI.',
    'Describe what you need to search Forma routines.':
      'Opisz, czego potrzebujesz, aby przeszukać plany Forma.',
    'Searching Forma routines...': 'Przeszukiwanie planów Forma...',
    'No strong Forma routine match yet. You can generate a custom draft.':
      'Nie znaleziono dobrego dopasowania. Możesz wygenerować własny szkic.',
    'For example: lower back mobility':
      'Na przykład: mobilność dolnego odcinka pleców',
    'Want a different combination?': 'Chcesz innego zestawu?',
    'No strong match? Create a custom draft.':
      'Brak dobrego dopasowania? Utwórz własny szkic.',
    'Create something different': 'Utwórz coś innego',
    'Generate custom routine': 'Wygeneruj własny plan',
    'Back to search': 'Wróć do wyszukiwania',
    'Back to Forma': 'Wróć do Formy',
    '© {year} Forma. All rights reserved.':
      '© {year} Forma. Wszelkie prawa zastrzeżone.',
    'Page not found': 'Nie znaleziono strony',
    'This page does not exist or may have moved.':
      'Ta strona nie istnieje lub została przeniesiona.',
    'Thanks for training with Forma.': 'Dzięki, że trenujesz z Formą.',
    'Forma Trainer': 'Forma dla trenerów',
    'Forma for professionals': 'Forma dla specjalistów',
    'For professionals': 'Dla specjalistów',
    'Billing currency': 'Waluta rozliczeń',
    'Billing interval': 'Okres rozliczeniowy',
    Currency: 'Waluta',
    Activity: 'Aktywność',
    'Loading weekly activity...': 'Ładowanie aktywności z tego tygodnia...',
    'Weekly activity could not load.':
      'Nie udało się załadować aktywności z tego tygodnia.',
    'Your routine is still ready when you are.':
      'Twój plan nadal czeka, gdy będziesz gotowy.',
    'Track completed exercises and full routine days in a simple monthly view.':
      'Śledź ukończone ćwiczenia i dni z pełnym planem w prostym widoku miesięcznym.',
    'Show previous month': 'Pokaż poprzedni miesiąc',
    'Show next month': 'Pokaż następny miesiąc',
    'Full routine completed': 'Ukończono cały plan',
    'Exercise completed': 'Ukończono ćwiczenie',
    'Manual workout': 'Trening ręczny',
    'Manual workout marked': 'Oznaczono trening ręcznie',
    'No activity recorded': 'Brak zapisanej aktywności',
    Today: 'Dzisiaj',
    'Back to today': 'Wróć do dzisiaj',
    'Not yet': 'Jeszcze nie',
    'Active days this month': 'Aktywne dni w tym miesiącu',
    '{completed} of {total} exercises': '{completed} z {total} ćwiczeń',
    'Activity could not load.': 'Nie udało się załadować aktywności.',
    'This activity was recorded from an in-app workout.':
      'Ta aktywność została zapisana z treningu w aplikacji.',
    'Remove manual mark': 'Usuń ręczne oznaczenie',
    'Mark workout done': 'Oznacz trening jako wykonany',
    'Activity service is not available.': 'Usługa aktywności jest niedostępna.',
    'Workout marked.': 'Trening oznaczony.',
    'Manual workout removed.': 'Ręczne oznaczenie treningu usunięte.',
    'Manage your trainer plan and billing details.':
      'Zarządzaj abonamentem trenera i szczegółami rozliczeń.',
    'Activity could not be updated.': 'Nie udało się zaktualizować aktywności.',
    'Sign in before updating activity.':
      'Zaloguj się, zanim zaktualizujesz aktywność.',
    'That activity date cannot be updated.':
      'Nie można zaktualizować tej daty aktywności.',
    'The activity service is temporarily unavailable.':
      'Usługa aktywności jest tymczasowo niedostępna.',
    'Activity service error: {code}': 'Błąd usługi aktywności: {code}',
    Loading: 'Ładowanie',
    Notifications: 'Powiadomienia',
    Reminders: 'Przypomnienia',
    'Manage reminder schedules and delivery on this device.':
      'Zarządzaj harmonogramami przypomnień i powiadomieniami na tym urządzeniu.',
    'Choose a routine and when Forma should remind you.':
      'Wybierz plan i określ, kiedy Forma ma Ci o nim przypominać.',
    'Build routine reminders for daily workouts or hourly movement breaks.':
      'Twórz przypomnienia o codziennych treningach albo przerwach na ruch w ciągu dnia.',
    'Build weekly routine reminders or movement breaks during the day.':
      'Twórz tygodniowe przypomnienia o planach albo przerwach na ruch w ciągu dnia.',
    'Install the app for iPhone notifications':
      'Zainstaluj aplikację, aby mieć powiadomienia na iPhonie',
    'On iPhone, push notifications work best from the installed Home Screen app. Open the install guide, add Forma to your Home Screen, then enable notifications there.':
      'Na iPhonie powiadomienia push najlepiej działają z aplikacji dodanej do ekranu głównego. Otwórz instrukcję instalacji, dodaj Formę do ekranu głównego i tam włącz powiadomienia.',
    iPhone: 'iPhone',
    'Open install guide': 'Otwórz instrukcję instalacji',
    'Push notifications': 'Powiadomienia push',
    'Notifications on this device': 'Powiadomienia na tym urządzeniu',
    'Install required': 'Wymagana instalacja',
    'Install Forma on your Home Screen before enabling notifications on iPhone.':
      'Dodaj Formę do ekranu głównego, zanim włączysz powiadomienia na iPhonie.',
    'Install Forma': 'Zainstaluj Formę',
    'Enable notifications': 'Włącz powiadomienia',
    'Connect this device': 'Połącz to urządzenie',
    'Edit reminder': 'Edytuj przypomnienie',
    'Add reminder': 'Dodaj przypomnienie',
    'Link each reminder to a routine and choose how often it should nudge you.':
      'Połącz każde przypomnienie z planem i wybierz, jak często ma Ci o nim przypominać.',
    On: 'Wł.',
    Off: 'Wył.',
    Routine: 'Plan',
    'Choose a routine': 'Wybierz plan',
    Label: 'Etykieta',
    'Reminder name (optional)': 'Nazwa przypomnienia (opcjonalnie)',
    'The routine name will be used if you leave this empty.':
      'Jeśli zostawisz to pole puste, użyjemy nazwy planu.',
    Daily: 'Codziennie',
    'Reminder frequency': 'Częstotliwość przypomnienia',
    'At chosen times': 'O wybranej godzinie',
    'Weekly schedule': 'Harmonogram tygodniowy',
    'Repeat during day': 'Powtarzaj w ciągu dnia',
    'Repeat during the day': 'Co jakiś czas',
    'During day': 'W ciągu dnia',
    Times: 'Godziny',
    'Add another reminder if you need a second time.':
      'Dodaj osobne przypomnienie, jeśli potrzebujesz drugiej godziny.',
    'One reminder will be created for each time.':
      'Dla każdej godziny utworzymy osobne przypomnienie.',
    'Reminder time': 'Godzina przypomnienia',
    'Every (min)': 'Co (min)',
    Start: 'Start',
    End: 'Koniec',
    Days: 'Dni',
    Weekdays: 'Dni robocze',
    'Reminder status': 'Status przypomnienia',
    'You can pause it later without deleting it.':
      'Możesz je później wstrzymać bez usuwania.',
    Active: 'Aktywne',
    Paused: 'Wstrzymane',
    'Change status for {name}': 'Zmień status przypomnienia {name}',
    Back: 'Wstecz',
    'Save changes': 'Zapisz zmiany',
    'Save reminder': 'Zapisz przypomnienie',
    'Your reminders': 'Twoje przypomnienia',
    'New reminder': 'Nowe przypomnienie',
    New: 'Nowa',
    Edit: 'Edytuj',
    Delete: 'Usuń',
    'More actions': 'Więcej akcji',
    'No reminders yet. Add one for a daily workout or an hourly movement break.':
      'Nie ma jeszcze przypomnień. Dodaj jedno do codziennego treningu albo przerwy na ruch co godzinę.',
    'No reminders yet. Add one for a weekly routine or a movement break during the day.':
      'Nie ma jeszcze przypomnień. Dodaj jedno do tygodniowego planu albo przerwy ruchowej w ciągu dnia.',
    'No reminders yet.': 'Nie masz jeszcze przypomnień.',
    'Add one for a routine or a movement break during the day.':
      'Dodaj przypomnienie o planie albo przerwie na ruch w ciągu dnia.',
    'Reminders could not load.': 'Nie udało się załadować przypomnień.',
    Cancel: 'Anuluj',
    'Find routines': 'Znajdź plany',
    'Browse routines': 'Przeglądaj plany',
    'Find a routine for what you need today.':
      'Znajdź plan na to, czego dziś potrzebujesz.',
    Search: 'Szukaj',
    Goal: 'Cel',
    'What do you need?': 'Czego potrzebujesz?',
    Purpose: 'Cel',
    'Update the routine name or purpose.':
      'Zaktualizuj nazwę planu lub jego cel.',
    'Update routine details and focus areas.':
      'Zaktualizuj szczegóły planu i obszary ciała.',
    Endurance: 'Wytrzymałość',
    'Speed & Power': 'Szybkość i moc',
    Rehab: 'Rehab',
    Focus: 'Obszar',
    'Focus areas': 'Obszary ciała',
    Context: 'Kontekst',
    'Full body': 'Całe ciało',
    'Upper body': 'Górna część ciała',
    'Upper body short': 'Góra',
    'Lower body': 'Dolna część ciała',
    'Lower body short': 'Dolna',
    'Back / spine': 'Plecy / kręgosłup',
    Knees: 'Kolana',
    Shoulders: 'Barki',
    'Ankles / feet': 'Kostki / stopy',
    Desk: 'Biurko',
    Gym: 'Siłownia',
    Home: 'Dom',
    'No equipment': 'Bez sprzętu',
    'No gear': 'Bez sprzętu',
    'Body area': 'Obszar ciała',
    'Category / Activity': 'Kategoria / aktywność',
    Knee: 'Kolano',
    Hips: 'Biodra',
    Groin: 'Pachwina',
    Warmup: 'Rozgrzewka',
    Cooldown: 'Schłodzenie',
    'More filters': 'Więcej filtrów',
    'Fewer filters': 'Mniej filtrów',
    'Clear filters': 'Wyczyść filtry',
    Filters: 'Filtry',
    'Remove filter {label}': 'Usuń filtr {label}',
    'Found: {count}': 'Znaleziono: {count}',
    'Search by name': 'Szukaj po nazwie',
    'Clear search': 'Wyczyść wyszukiwanie',
    'Show more': 'Pokaż więcej',
    'Show more ({count})': 'Pokaż więcej ({count})',
    Added: 'Dodano',
    Difficulty: 'Poziom trudności',
    Duration: 'Czas trwania',
    'Up to 10 min': 'Do 10 min',
    '11–20 min': '11–20 min',
    '21–30 min': '21–30 min',
    '31+ min': '31+ min',
    'Showing {visible} of {total} routines':
      'Wyświetlono {visible} z {total} planów',
    'Soccer, mobility, core...': 'Piłka, mobilność, core...',
    'Knee recovery, warmup, hips...':
      'Regeneracja kolana, rozgrzewka, biodra...',
    'Add to my account': 'Dodaj do mojego konta',
    Preview: 'Podgląd',
    'Routine preview': 'Podgląd planu',
    '1 exercise': '1 ćwiczenie',
    '{count} exercises': '{count} ćwiczeń',
    'Loading routine preview...': 'Ładowanie podglądu planu...',
    'Updating routine preview...': 'Odświeżanie podglądu planu...',
    'Routine preview could not load.':
      'Nie udało się załadować podglądu planu.',
    'Routine preview could not load. Try again from the routine list.':
      'Nie udało się załadować podglądu planu. Spróbuj ponownie z listy planów.',
    'Exercises included': 'Ćwiczenia w zestawie',
    'No exercises listed yet.': 'Nie ma jeszcze ćwiczeń na liście.',
    'Adding routine...': 'Dodawanie planu...',
    'Recommended routines': 'Polecane plany',
    'Matching routines': 'Pasujące plany',
    All: 'Wszystkie',
    Included: 'Dostępne',
    'No Forma routines match those filters yet. Clear filters or try name search.':
      'Na razie żaden plan Formy nie pasuje do tych filtrów. Wyczyść filtry albo spróbuj wyszukać po nazwie.',
    'No recommended routines are available yet.':
      'Nie ma jeszcze dostępnych polecanych planów.',
    'Routines could not load.': 'Nie udało się załadować planów.',
    Close: 'Zamknij',
    'Signed in as': 'Zalogowano jako',
    'Sign out': 'Wyloguj',
    'Leave guest mode': 'Opuść tryb gościa',
    'Leaving guest mode...': 'Opuszczanie trybu gościa...',
    'Loading Forma': 'Ładowanie Formy',
    'Forma is still reconnecting.': 'Forma nadal łączy się ponownie.',
    'Plan status': 'Status abonamentu',
    'Plan status is unavailable offline.':
      'Status abonamentu jest niedostępny offline.',
    'Paid routines stay locked until Forma reconnects, but free routines and tools can keep loading.':
      'Płatne plany pozostają zablokowane, dopóki Forma nie połączy się ponownie, ale darmowe plany i narzędzia mogą nadal się ładować.',
    Profile: 'Profil',
    Reload: 'Odśwież',
    'You can keep using anything that has already loaded.':
      'Możesz nadal korzystać z tego, co już się załadowało.',
    'Create routine': 'Utwórz plan',
    'Add a new routine to organize your exercises.':
      'Dodaj nowy plan, aby uporządkować ćwiczenia.',
    Icon: 'Ikona',
    Category: 'Kategoria',
    Prehab: 'Prehab',
    'Routine name': 'Nazwa planu',
    'Routine note': 'Notatka planu',
    'When to use this routine, equipment, or overall intent.':
      'Kiedy używać tego planu, jaki sprzęt jest potrzebny albo jaki jest jego ogólny cel.',
    'Leg Day': 'Dzień nóg',
    Forma: 'Forma',
    "You train. We'll do the rest.": 'Trenuj. Resztę bierzemy na siebie.',
    'Firebase is restoring the current session.':
      'Firebase przywraca bieżącą sesję.',
    'Sign in to keep your routines, or continue as guest.':
      'Zaloguj się, aby zachować plany, albo kontynuuj jako gość.',
    'Sign in': 'Zaloguj się',
    'Sign in to Forma': 'Zaloguj się do Formy',
    'Create your Forma account': 'Załóż konto w Formie',
    'Signing in...': 'Logowanie...',
    'Save your guest account': 'Zapisz konto gościa',
    'Choose how you want to keep your routines and continue on other devices.':
      'Wybierz, jak chcesz zachować plany i korzystać z nich na innych urządzeniach.',
    'Choose a secure way to continue.':
      'Wybierz bezpieczny sposób kontynuowania.',
    'Choose Google or email to continue.':
      'Wybierz Google lub e-mail, aby kontynuować.',
    'Connecting to Google...': 'Łączenie z Google...',
    'Continue with Google': 'Kontynuuj z Google',
    'Continue with Apple': 'Kontynuuj z Apple',
    'Continue with email': 'Kontynuuj przez e-mail',
    'Create account': 'Utwórz konto',
    'Email sign-in mode': 'Tryb logowania e-mail',
    'Email address': 'Adres e-mail',
    Password: 'Hasło',
    'Forgot password?': 'Nie pamiętasz hasła?',
    'Password reset email sent.':
      'Wiadomość do zresetowania hasła została wysłana.',
    'This creates a password for your current guest account, so its routines stay with you.':
      'Utworzysz hasło do bieżącego konta gościa, dzięki czemu jego plany zostaną z Tobą.',
    'Starting guest session...': 'Uruchamianie sesji gościa...',
    'Continue as guest': 'Kontynuuj jako gość',
    'Started as guest.': 'Rozpoczęto jako gość.',
    'Guest account': 'Konto gościa',
    'Guest account saved successfully.': 'Konto gościa zostało zapisane.',
    'Account created successfully.': 'Konto zostało utworzone.',
    'Sign in to connect your watch.': 'Zaloguj się, aby połączyć zegarek.',
    'Finish signing in to connect your watch.':
      'Dokończ logowanie, aby połączyć zegarek.',
    'Guest mode is temporary. Sign in later to keep your routines.':
      'Tryb gościa jest tymczasowy. Zaloguj się później, aby zachować plany.',
    'What you can do in Forma': 'Co możesz zrobić w Formie',
    'Start with a ready-made training plan.':
      'Zacznij od gotowego planu treningowego.',
    'Train step by step': 'Ćwicz krok po kroku',
    'Keep each exercise and its guidance close.':
      'Miej każde ćwiczenie i wskazówki pod ręką.',
    'See your consistency': 'Śledź regularność',
    'View completed workouts in Activity.':
      'Ukończone treningi zobaczysz w Aktywności.',
    'See the full walkthrough': 'Zobacz pełny przewodnik',
    'Guest account. Sign in to keep your routines.':
      'Konto gościa. Zaloguj się, aby zachować plany.',
    'Connect a sign-in method before creating reminders.':
      'Najpierw dodaj metodę logowania, aby tworzyć przypomnienia.',
    'Step 1 of 2': 'Krok 1 z 2',
    'Step 2 of 2': 'Krok 2 z 2',
    'Onboarding progress': 'Postęp konfiguracji',
    'Make Forma yours': 'Dopasuj Formę do siebie',
    'Shape your recommendations': 'Dopasuj rekomendacje',
    'What do you enjoy doing?': 'Jak lubisz się ruszać?',
    'What should Forma help you with?': 'W czym Forma ma Ci pomóc?',
    'What would you like to improve?': 'Co chcesz poprawić?',
    "Choose what fits you. We'll show the most relevant routines first, while the full library stays available.":
      'Wybierz to, co do Ciebie pasuje. Najpierw pokażemy najlepiej dopasowane plany, a cała biblioteka nadal będzie dostępna.',
    'Choose your interests': 'Wybierz swoje aktywności',
    'Choose your goals': 'Wybierz swoje cele',
    'Choose your equipment': 'Wybierz swój sprzęt',
    'What do you want to work on?': 'Nad czym chcesz pracować?',
    'What equipment do you have?': 'Jaki masz sprzęt?',
    'Optional. Pick any that apply.':
      'Opcjonalnie. Wybierz dowolne, które do Ciebie pasują.',
    'Choose your usual setup.': 'Wybierz sprzęt, który zwykle masz pod ręką.',
    'Resistance bands, a foam roller, a pull-up bar.':
      'Gumy oporowe, wałek do rolowania, drążek.',
    'Ease off and settle after effort.':
      'Wycisz się i uspokój organizm po wysiłku.',
    'Choose any preferences, or skip for now.':
      'Wybierz dowolne preferencje albo pomiń ten krok.',
    'Selected preferences: {count}': 'Wybrane preferencje: {count}',
    Continue: 'Dalej',
    'Show routines': 'Pokaż plany',
    'Saving preferences...': 'Zapisywanie preferencji...',
    'Save preferences': 'Zapisz preferencje',
    'Preferences saved.': 'Preferencje zapisane.',
    'Personalize your recommendations': 'Dopasuj swoje rekomendacje',
    'Pick your goals and equipment, and Forma will show matching routines first. Every routine stays available.':
      'Wybierz cele i sprzęt, a Forma najpierw pokaże pasujące plany. Wszystkie plany nadal będą dostępne.',
    'Choose preferences': 'Wybierz preferencje',
    'Routine recommendations': 'Rekomendacje planów',
    'Choose the goals and equipment Forma should use for recommendations.':
      'Wybierz cele i sprzęt, które Forma ma uwzględniać w rekomendacjach.',
    '+{count} more': '+{count} więcej',
    'Edit preferences': 'Edytuj preferencje',
    'Explore all routines': 'Przeglądaj wszystkie plany',
    'Your collection': 'Twoja kolekcja',
    'No personalized routines are available yet.':
      'Nie ma jeszcze dostępnych dopasowanych planów.',
    'No routines match your equipment yet. Update your preferences or explore all routines.':
      'Żaden plan nie pasuje jeszcze do Twojego sprzętu. Zmień preferencje albo przejrzyj wszystkie plany.',
    'Personalized routines could not load.':
      'Nie udało się załadować dopasowanych planów.',
    'Loading personalized routines...': 'Ładowanie dopasowanych planów...',
    '{minutes} min': '{minutes} min',
    Beginner: 'Początkujący',
    Intermediate: 'Średniozaawansowany',
    Advanced: 'Zaawansowany',
    Mat: 'Mata',
    'Resistance band': 'Guma oporowa',
    Chair: 'Krzesło',
    'Foam roller': 'Wałek',
    Weights: 'Ciężary',
    'View with Forma Plus': 'Zobacz z Forma Plus',
    'Checking access...': 'Sprawdzanie dostępu...',
    'Choices not saved': 'Nie zapisano wyboru',
    "We couldn't save your choices. Check your connection and try again.":
      'Nie udało się zapisać wyboru. Sprawdź połączenie i spróbuj ponownie.',
    "We couldn't save your choices. Reload the page and try again.":
      'Nie udało się zapisać wyboru. Odśwież stronę i spróbuj ponownie.',
    'For you': 'Dla Ciebie',
    'Recommended for you': 'Polecane dla Ciebie',
    'Popular routines': 'Popularne plany',
    Preferences: 'Preferencje',
    'Explore all': 'Przeglądaj wszystkie',
    'Back to for you': 'Wróć do propozycji',
    'Matches {preferences}': 'Pasuje do: {preferences}',
    'Everyday movement': 'Codzienny ruch',
    'Move more comfortably through busy days.':
      'Ruszaj się swobodniej w zabiegane dni.',
    Yoga: 'Joga',
    'Explore mobility, balance, and calmer movement.':
      'Rozwijaj mobilność, równowagę i spokojniejszy ruch.',
    Pilates: 'Pilates',
    'Develop controlled strength and posture.':
      'Rozwijaj siłę, kontrolę i postawę.',
    'Strength & gym': 'Trening siłowy',
    'Prepare to lift and recover between sessions.':
      'Przygotuj się do treningu siłowego i regeneruj między sesjami.',
    'Warm up': 'Rozgrzewka',
    'Feel ready before activity.': 'Przygotuj ciało przed aktywnością.',
    'Reset after training or a long day.':
      'Zregeneruj się po treningu albo długim dniu.',
    'Move with more comfort and range.':
      'Ruszaj się swobodniej i w większym zakresie.',
    'Build strength': 'Buduj siłę',
    'Create a stronger base for movement.': 'Zbuduj silniejszą bazę do ruchu.',
    'Bodyweight only': 'Tylko masa ciała',
    'Your previous weights choice could mean home or gym. Choose your setup again.':
      'Poprzedni wybór ciężarów mógł oznaczać dom lub siłownię. Wybierz swój zestaw ponownie.',
    'Bands and small gear': 'Gumy i mały sprzęt',
    'Joint resilience': 'Odporność stawów',
    'Support knees and other hard-working joints.':
      'Wspieraj kolana i inne mocno pracujące stawy.',
    'Slow down and release tension.': 'Zwolnij i rozluźnij napięcie.',
    'Current routine': 'Aktualny plan',
    Details: 'Szczegóły',
    'Switch routine': 'Zmień plan',
    'Pick the routine you want to work with today.':
      'Wybierz plan, z którym chcesz dziś ćwiczyć.',
    'Set reminder': 'Ustaw przypomnienie',
    "Today's routine is complete": 'Dzisiejszy trening ukończony',
    'Routine selection': 'Wybór planu',
    'Trainer routines': 'Plany od trenera',
    'My routines': 'Moje plany',
    'From trainer': 'Od trenera',
    Personal: 'Własna',
    Selected: 'Wybrana',
    'No routine from your trainer yet.': 'Trener nie przypisał jeszcze planu.',
    'When your trainer prepares a plan, it will appear here automatically. You can keep using your own routines.':
      'Gdy trener przygotuje plan, pojawi się tutaj automatycznie. Nadal możesz korzystać ze swoich planów.',
    'Your plan is being prepared': 'Twój plan jest w przygotowaniu',
    'Your trainer has not assigned a routine yet. It will appear here automatically when it is ready.':
      'Trener nie przypisał jeszcze planu. Gdy plan będzie gotowy, pojawi się tutaj automatycznie.',
    'In the meantime, you can choose a Forma routine or create your own.':
      'W międzyczasie możesz wybrać plan Formy albo utworzyć własny.',
    'Create your first routine': 'Utwórz swój pierwszy plan treningowy',
    'Getting started': 'Pierwsze kroki',
    'Choose your first routine': 'Wybierz pierwszy plan treningowy',
    'Start from a Forma routine if you want a guided setup, or create your own routine from scratch.':
      'Zacznij od planu Formy, jeśli chcesz gotowy punkt startowy, albo utwórz własny plan od zera.',
    'Start with a Forma routine': 'Zacznij od planu Formy',
    'Pick a curated starter routine to begin.':
      'Wybierz sprawdzony plan startowy i zacznij od niego.',
    'Search starter routines': 'Szukaj planów startowych',
    'Mobility, strength, recovery...': 'Mobilność, siła, regeneracja...',
    'Recommended starters': 'Polecane plany startowe',
    'Popular starter routines': 'Popularne plany startowe',
    'Search results': 'Wyniki wyszukiwania',
    'Loading starter routines...': 'Ładowanie planów startowych...',
    'Starter routines could not load.':
      'Nie udało się załadować planów startowych.',
    'Use this routine': 'Użyj tego planu',
    'No starter routines match that search yet.':
      'Żaden plan startowy jeszcze nie pasuje do tego wyszukiwania.',
    'No starter routines are available yet. You can still create your own routine now.':
      'Nie ma jeszcze dostępnych planów startowych. Wciąż możesz utworzyć własny plan.',
    'Create my own routine': 'Utwórz własny plan treningowy',
    'Prefer to build your own from scratch?': 'Wolisz zbudować własną od zera?',
    'Name a routine, choose an icon, then add exercises one by one.':
      'Nazwij plan, wybierz ikonę, a potem dodawaj ćwiczenia jedno po drugim.',
    'Name a routine, choose a category, then add exercises one by one.':
      'Nazwij plan, wybierz kategorię, a potem dodawaj ćwiczenia jedno po drugim.',
    'Loading routines...': 'Ładowanie planów...',
    'Updating routines...': 'Odświeżanie planów...',
    'This is taking longer than usual.': 'To trwa dłużej niż zwykle.',
    'You are offline. Reconnect and try again.':
      'Jesteś offline. Połącz się ponownie i spróbuj jeszcze raz.',
    'Try again': 'Spróbuj ponownie',
    'Delete routine': 'Usuń plan',
    'Select a routine first.': 'Najpierw wybierz plan.',
    'Edit routine': 'Edytuj plan',
    'Update the routine name or icon.': 'Zaktualizuj nazwę planu lub ikonę.',
    'Update the routine name or category.':
      'Zaktualizuj nazwę planu lub kategorię.',
    'Add exercise': 'Dodaj ćwiczenie',
    'Review exercise': 'Sprawdź ćwiczenie',
    'Confirm this exercise before adding it to {name}.':
      'Sprawdź ćwiczenie przed dodaniem go do planu „{name}”.',
    'Confirm and add': 'Potwierdź i dodaj',
    'Choose an exercise to add to this routine.':
      'Wybierz ćwiczenie, które chcesz dodać do tego planu.',
    'Pick existing exercise': 'Wybierz istniejące ćwiczenie',
    'Reuse a private exercise you already saved.':
      'Użyj zapisanego prywatnego ćwiczenia.',
    'Use Forma library exercises or your saved private exercises.':
      'Użyj ćwiczeń z biblioteki Formy albo własnych zapisanych ćwiczeń.',
    'My exercises': 'Moje ćwiczenia',
    'Forma library': 'Biblioteka Formy',
    Free: 'Bezpłatne',
    'Needs Forma Plus': 'Wymaga Forma Plus',
    'Unlock Forma Plus to add this exercise.':
      'Odblokuj Forma Plus, aby dodać to ćwiczenie.',
    'Create a new reusable exercise for this routine.':
      'Utwórz nowe ćwiczenie do ponownego użycia w tym planie.',
    'Reuse one of your private exercises, or create a new reusable exercise.':
      'Użyj jednego ze swoich prywatnych ćwiczeń albo utwórz nowe do ponownego użycia.',
    'Create reusable exercise': 'Utwórz ćwiczenie do ponownego użycia',
    'New exercise': 'Nowe ćwiczenie',
    'Save this exercise to your private library and add a copy to the routine.':
      'Zapisz to ćwiczenie w prywatnej bibliotece i dodaj kopię do planu.',
    'We will add it to this routine and save it in your library.':
      'Dodamy je do tego planu i zapiszemy w Twojej bibliotece.',
    'Save and add': 'Zapisz i dodaj',
    'Search exercises': 'Szukaj ćwiczeń',
    'Search by exercise name or note':
      'Szukaj według nazwy ćwiczenia lub notatki',
    '{title} video demo': 'Film instruktażowy: {title}',
    'Exercise source': 'Źródło ćwiczeń',
    'All sources': 'Wszystkie źródła',
    'Filter exercises': 'Filtruj ćwiczenia',
    'Filter exercise source': 'Filtruj źródło ćwiczeń',
    'All exercises': 'Wszystkie ćwiczenia',
    Suggested: 'Polecane',
    Mine: 'Moje',
    'Quick exercise filters': 'Szybkie filtry ćwiczeń',
    'Muscle group': 'Grupa mięśni',
    'Filter by muscle group': 'Filtruj według grupy mięśni',
    'Narrow the library by equipment, level, and guidance.':
      'Zawęź bibliotekę według sprzętu, poziomu i wskazówek.',
    'Guidance and video': 'Wskazówki i wideo',
    'Filter by guidance and video': 'Filtruj według wskazówek i wideo',
    'Show {count} results': 'Pokaż wyniki: {count}',
    'Filters, {count} active': 'Filtry, aktywne: {count}',
    Unassigned: 'Nieprzypisane',
    'Filter by assignment': 'Filtruj według przypisania',
    Assign: 'Przypisz',
    'Assign {name}': 'Przypisz: {name}',
    'Choose a client': 'Wybierz klienta',
    'Routine: {routine}': 'Plan: {routine}',
    'Already assigned': 'Już przypisano',
    'No clients match that search.': 'Żaden klient nie pasuje do wyszukiwania.',
    'Add a client before assigning this routine.':
      'Dodaj klienta, zanim przypiszesz ten plan.',
    'Filter by equipment': 'Filtruj według sprzętu',
    Equipment: 'Sprzęt',
    Level: 'Poziom',
    'Filter by level': 'Filtruj według poziomu',
    'Glutes & hips': 'Pośladki i biodra',
    Glutes: 'Pośladki',
    Quads: 'Czworogłowe',
    Hamstrings: 'Dwugłowe',
    Calves: 'Łydki',
    Chest: 'Klatka piersiowa',
    'Back & lats': 'Plecy',
    'Traps & neck': 'Kaptury i szyja',
    Traps: 'Kaptury',
    Arms: 'Ramiona',
    Bodyweight: 'Masa własna',
    Dumbbells: 'Hantle',
    'Barbells & plates': 'Sztangi i obciążenia',
    Barbells: 'Sztangi',
    Kettlebells: 'Kettlebelle',
    'Machines & cables': 'Maszyny i wyciągi',
    Machines: 'Maszyny',
    'Bands & small gear': 'Gumy i mały sprzęt',
    'Small gear': 'Mały sprzęt',
    'Suggested exercises': 'Polecane ćwiczenia',
    'Matching exercises': 'Pasujące ćwiczenia',
    'Variations: {count}': 'Warianty: {count}',
    '{count} variants': '{count} warianty',
    'Show variants': 'Pokaż warianty',
    'Hide variants': 'Ukryj warianty',
    'Choose a variant': 'Wybierz wariant',
    'Variant {number}': 'Wariant {number}',
    'Recommended variant': 'Polecany',
    'Add variant {number}': 'Dodaj wariant {number}',
    'Full description': 'Pełny opis',
    'Hide description': 'Ukryj opis',
    'With video': 'Z wideo',
    'With guidance': 'Ze wskazówkami',
    'Missing guidance': 'Bez wskazówek',
    'Exercise library could not load.':
      'Nie udało się załadować biblioteki ćwiczeń.',
    'Close and reopen this dialog to try again.':
      'Zamknij i otwórz to okno ponownie, aby spróbować jeszcze raz.',
    'No reusable exercises yet': 'Nie ma jeszcze ćwiczeń do ponownego użycia',
    'Create one now and it will be available for your other routines.':
      'Utwórz pierwsze ćwiczenie, a będzie dostępne w Twoich innych planach.',
    'Curated exercises will appear here after the MoveKit sync runs.':
      'Wybrane ćwiczenia pojawią się tutaj po synchronizacji MoveKit.',
    'Create exercises once, then reuse them when building trainer routines.':
      'Twórz ćwiczenia raz i używaj ich ponownie przy układaniu planów trenerskich.',
    'Create exercise': 'Utwórz ćwiczenie',
    'No exercises match that search':
      'Żadne ćwiczenie nie pasuje do tego wyszukiwania',
    'No exercises match those filters':
      'Żadne ćwiczenie nie pasuje do tych filtrów',
    'Clear the search or create a new reusable exercise.':
      'Wyczyść wyszukiwanie albo utwórz nowe ćwiczenie do ponownego użycia.',
    'Try another search or filter, or create a new reusable exercise.':
      'Spróbuj innego wyszukiwania lub filtra albo utwórz nowe ćwiczenie.',
    'Clear the search or start from a blank exercise.':
      'Wyczyść wyszukiwanie albo zacznij od pustego ćwiczenia.',
    'Clear the search or filters, or start from a blank exercise.':
      'Wyczyść wyszukiwanie lub filtry albo zacznij od pustego ćwiczenia.',
    'Start from a blank exercise to build your first one.':
      'Zacznij od pustego ćwiczenia, aby utworzyć pierwsze.',
    'Showing {visible} of {total} exercises':
      'Widocznych {visible} z {total} ćwiczeń',
    'Clear the search or filters, or create a new reusable exercise.':
      'Wyczyść wyszukiwanie lub filtry albo utwórz nowe ćwiczenie do ponownego użycia.',
    'Choose how you want to start this reusable exercise.':
      'Wybierz, od czego zacząć tworzenie tego ćwiczenia.',
    'Start from existing': 'Zacznij od istniejącego',
    'Start from existing exercise': 'Zacznij od istniejącego ćwiczenia',
    'Pick a saved exercise to copy its targets and guidance.':
      'Wybierz zapisane ćwiczenie, aby skopiować jego cele i wskazówki.',
    'Copy targets and notes from a saved exercise.':
      'Skopiuj cele i notatki z zapisanego ćwiczenia.',
    'Start blank': 'Zacznij od zera',
    'Name a movement and add details from scratch.':
      'Nazwij ćwiczenie i dodaj szczegóły od zera.',
    'Start with the details from {name}, then adjust anything that should be different.':
      'Zacznij od szczegółów z ćwiczenia {name}, a potem zmień wszystko, co ma być inne.',
    'Name the movement, then add workout targets, coaching notes, or a demo when they help.':
      'Nazwij ćwiczenie, a potem dodaj cele treningowe, wskazówki albo demo, jeśli się przydadzą.',
    Title: 'Tytuł',
    'Exercise name': 'Nazwa ćwiczenia',
    'Plank Hold': 'Deska statyczna',
    'Video linked': 'Dodano wideo',
    'Video link': 'Link do wideo',
    'Demo video link': 'Link do demo wideo',
    'See how to perform this exercise.': 'Zobacz, jak wykonać to ćwiczenie.',
    'Add a YouTube, Instagram, TikTok, Vimeo, or direct video link.':
      'Dodaj link z YouTube, Instagrama, TikToka, Vimeo lub bezpośredni link do wideo.',
    'Paste the full link. Forma never plays videos automatically in the editor.':
      'Wklej pełny link. Forma nigdy nie odtwarza wideo automatycznie w edytorze.',
    'Paste a complete http:// or https:// link.':
      'Wklej pełny link zaczynający się od http:// lub https://.',
    '{source} is ready to play in Forma.':
      '{source} jest gotowy do odtworzenia w Formie.',
    '{source} is ready and will open outside Forma.':
      '{source} jest gotowy i otworzy się poza Formą.',
    'Notes added': 'Dodano notatki',
    'Guidance added': 'Dodano wskazówki',
    'Workout behavior': 'Sposób wykonania',
    'Both sides': 'Na obie strony',
    'Guided holds': 'Prowadzone utrzymania',
    'Reps only': 'Tylko powtórzenia',
    'Switch to reps only': 'Przełącz na same powtórzenia',
    'Switch to guided holds': 'Przełącz na prowadzone utrzymania',
    '2 sides': '2 strony',
    'Left and right side each count.':
      'Lewą i prawą stronę wykonuje się osobno.',
    'Tempo (optional)': 'Tempo (opcjonalnie)',
    'Adds per-rep down/up timing. Use both fields for tempo reps; hold-only reps need at least 10s.':
      'Dodaje czas opuszczania/podnoszenia na powtórzenie. Użyj obu pól dla powtórzeń w tempie; powtórzenia z samym trzymaniem potrzebują co najmniej 10 s.',
    'Down (eccentric)': 'Opuszczanie (ekscentryczna)',
    'Up (concentric)': 'Podnoszenie (koncentryczna)',
    'Down (s)': 'Opuszczanie (s)',
    'Up (s)': 'Podnoszenie (s)',
    'Hide helpful details': 'Ukryj pomocne szczegóły',
    'Hide details': 'Ukryj szczegóły',
    'Add helpful details': 'Dodaj pomocne szczegóły',
    'Add details (optional)': 'Dodaj szczegóły (opcjonalnie)',
    'YouTube video': 'Film YouTube',
    Series: 'Serie',
    Reps: 'Powtórzenia',
    Time: 'Czas',
    'Hold (s)': 'Czas trzymania (s)',
    Rest: 'Przerwa',
    'Rest (s)': 'Przerwa (s)',
    'Workout targets (optional)': 'Cele ćwiczenia (opcjonalnie)',
    'Leave fields empty to keep this as a simple confirmation step.':
      'Zostaw pola puste, aby ćwiczenie było tylko do odhaczenia.',
    'e.g. 3': 'np. 3',
    'e.g. 12': 'np. 12',
    'e.g. 45': 'np. 45',
    'e.g. 30': 'np. 30',
    'e.g. 2': 'np. 2',
    'e.g. 1': 'np. 1',
    'Reps-only exercises use one confirmation at the end. Hold-only reps run as isometric reps when Hold is at least 10s. Rest becomes active between sets.':
      'Ćwiczenia oparte tylko na powtórzeniach potwierdzasz raz, na końcu. Jeśli powtórzenie ma samo trzymanie, Czas trzymania musi wynosić co najmniej 10 s, aby działało jako izometria. Przerwa włącza się między seriami.',
    Notes: 'Notatki',
    'Beginner coaching': 'Wskazówki dla początkujących',
    'Exercise instructions': 'Instrukcje dla ćwiczącego',
    'Add short coaching rows for setup, live cues, and corrections.':
      'Dodaj kroki, wskazówki w trakcie ćwiczenia i najczęstsze błędy.',
    Steps: 'Kroki',
    'Stand tall.': 'Stań wysoko.',
    'Stand upright.': 'Stań prosto.',
    'Slow and controlled.': 'Powoli i z kontrolą.',
    'Rushing the reps.': 'Pośpiech w powtórzeniach.',
    'Add step': 'Dodaj krok',
    'Add cue': 'Dodaj wskazówkę',
    'Add mistake': 'Dodaj błąd',
    'Add common mistake': 'Dodaj częsty błąd',
    'Remove item': 'Usuń pozycję',
    'Context, modification, or safety note.':
      'Kontekst, modyfikacja albo uwaga bezpieczeństwa.',
    'e.g. easier variation or a safety note.':
      'Np. łatwiejszy wariant lub uwaga dotycząca bezpieczeństwa.',
    '{field} supports up to {max} items.':
      '{field} obsługuje maksymalnie {max} pozycji.',
    'Short coaching cues or setup notes.':
      'Krótkie wskazówki techniczne lub notatki przygotowawcze.',
    'Watch demo': 'Zobacz demo',
    'Exercise demo': 'Demo ćwiczenia',
    'Opens on YouTube so the form stays lightweight.':
      'Otwiera się w YouTube, dzięki czemu aplikacja pozostaje lekka.',
    'Save exercise': 'Zapisz ćwiczenie',
    'Edit reusable exercise': 'Edytuj ćwiczenie do ponownego użycia',
    'Update this private library exercise for future trainer routines.':
      'Zaktualizuj to ćwiczenie z prywatnej biblioteki na potrzeby przyszłych planów trenerskich.',
    'Save this exercise to your private library so it is ready for future routines.':
      'Zapisz to ćwiczenie w prywatnej bibliotece, aby było gotowe do przyszłych planów.',
    'Save it to your private library so it is ready for future routines.':
      'Zapisz je w prywatnej bibliotece, aby było gotowe do przyszłych planów.',
    'Routine Exercises': 'Ćwiczenia w planie',
    '{name} exercises': 'Ćwiczenia: {name}',
    'No routine selected': 'Nie wybrano planu',
    'Move exercises with the arrows. Tap Done when the order feels right.':
      'Przesuwaj ćwiczenia strzałkami. Kliknij Gotowe, gdy kolejność się zgadza.',
    Done: 'Gotowe',
    'No exercises yet': 'Nie ma jeszcze ćwiczeń',
    'Add the first exercise to this routine.':
      'Dodaj pierwsze ćwiczenie do tego planu.',
    'Create a routine first, then add exercises to it.':
      'Najpierw utwórz plan, a potem dodaj do niego ćwiczenia.',
    'Start routine': 'Rozpocznij plan',
    'Continue routine': 'Kontynuuj plan',
    'Repeat routine': 'Powtórz plan',
    'Done today': 'Wykonano dzisiaj',
    'Ready for your first routine?': 'Czas na pierwszy plan treningowy?',
    'Use Start routine when you want Forma to guide each exercise and save your progress. You can still choose any exercise below.':
      'Użyj opcji „Rozpocznij plan”, gdy chcesz, aby Forma poprowadziła każde ćwiczenie i zapisała Twój postęp. Nadal możesz wybrać dowolne ćwiczenie poniżej.',
    'Got it': 'Rozumiem',
    'Ready for your first session?': 'Czas na pierwszą sesję?',
    'Start with the first exercise. Forma will guide the timing and save your progress.':
      'Zacznij od pierwszego ćwiczenia. Forma poprowadzi czas i zapisze Twój postęp.',
    'Start first exercise': 'Zacznij pierwsze ćwiczenie',
    'Exercises could not load.': 'Nie udało się załadować ćwiczeń.',
    'Refresh the page or try again in a moment.':
      'Odśwież stronę albo spróbuj ponownie za chwilę.',
    'Edit exercise': 'Edytuj ćwiczenie',
    'Exercise details': 'Szczegóły ćwiczenia',
    'Review exercise targets, coaching notes, and demo.':
      'Przejrzyj cele ćwiczenia, wskazówki trenerskie i demo.',
    'Update the title and adjust any helpful details you need.':
      'Zmień tytuł i dopracuj potrzebne szczegóły.',
    'Preview only': 'Tylko podgląd',
    'This exercise is read-only here. Use it as a reference or create your own version before editing.':
      'To ćwiczenie jest tutaj tylko do odczytu. Potraktuj je jako wzorzec albo utwórz własną wersję, jeśli chcesz je edytować.',
    'Read-only': 'Tylko do odczytu',
    'Edit helpful details': 'Edytuj pomocne szczegóły',
    'View helpful details': 'Pokaż pomocne szczegóły',
    'Opens on YouTube so the editor stays compact.':
      'Otwiera się w YouTube, dzięki czemu edytor pozostaje kompaktowy.',
    'Workout view': 'Widok treningu',
    'Exercise preview': 'Podgląd ćwiczenia',
    'Follow the exercise and keep your progress moving.':
      'Wykonuj ćwiczenie i trzymaj rytm.',
    'Completed: {title}': 'Ukończono: {title}',
    '{title} is complete.': 'Ćwiczenie „{title}” ukończone.',
    'Take a moment to reset, then start the next exercise when you are ready.':
      'Daj sobie chwilę na reset, a potem zacznij kolejne ćwiczenie.',
    'Up next': 'Następne',
    'Demo is ready if you want a quick look.':
      'Demo jest gotowe, jeśli chcesz szybko zerknąć.',
    'Setup notes are available before you start.':
      'Przed startem możesz sprawdzić notatki przygotowawcze.',
    'Notes are available before you start.':
      'Przed startem możesz sprawdzić notatki.',
    'Guidance ready': 'Wskazówki gotowe',
    Target: 'Cel',
    'Start exercise': 'Zacznij ćwiczenie',
    'Start next exercise': 'Zacznij kolejne ćwiczenie',
    'Next step': 'Następny krok',
    'Exercise guide': 'Wskazówki do ćwiczenia',
    'Open video on {provider}': 'Otwórz film w {provider}',
    'Exercise format': 'Format ćwiczenia',
    Guide: 'Wskazówki',
    'Hide guidance': 'Ukryj wskazówki',
    'Show guidance': 'Pokaż wskazówki',
    'How to do it': 'Jak wykonać',
    Cues: 'Wskazówki',
    'Common mistakes': 'Częste błędy',
    'Additional notes': 'Dodatkowe notatki',
    'Exercise {current} of {total}': 'Ćwiczenie {current} z {total}',
    'Left side': 'Lewa strona',
    'Right side': 'Prawa strona',
    'Target: {seconds}s per side': 'Cel: {seconds}s na stronę',
    'Finish the left timer, then repeat on the right side.':
      'Dokończ timer lewej strony, potem powtórz na prawej.',
    Video: 'Wideo',
    'Video demo': 'Demo wideo',
    'Demo not loaded': 'Demo się nie wczytało',
    'Try again in a moment.': 'Spróbuj ponownie za chwilę.',
    'Guided Step': 'Prowadzony krok',
    'Complete When Ready': 'Ukończ, gdy skończysz',
    'Follow Along': 'Ćwicz według wskazówek',
    Review: 'Przegląd',
    'Follow the guidance for this step, then mark it complete when you are done.':
      'Wykonaj ten krok według wskazówek, a po zakończeniu oznacz go jako ukończony.',
    'Use the notes below or open the demo if you want extra guidance.':
      'Skorzystaj z poniższych notatek albo otwórz demo, jeśli chcesz dodatkowych wskazówek.',
    'Use the notes below for setup and coaching cues.':
      'Skorzystaj z poniższych notatek przygotowawczych i wskazówek technicznych.',
    'Use the guide above or open the demo if you want extra guidance.':
      'Skorzystaj z powyższych wskazówek albo otwórz demo, jeśli chcesz dodatkowej pomocy.',
    'Use the guide above for setup and coaching cues.':
      'Skorzystaj z powyższych wskazówek dotyczących ustawienia i techniki.',
    'Open the demo anytime if you want a visual reference.':
      'Otwórz demo w dowolnym momencie, jeśli chcesz zobaczyć przykład.',
    'There is no timer or target here, just a single guided completion.':
      'Tutaj nie ma timera ani celu, tylko jeden krok do odhaczenia ze wskazówkami.',
    'Follow the notes or demo, then mark this step complete.':
      'Skorzystaj z notatek lub demo, a potem oznacz ten krok jako ukończony.',
    'Review the exercise values, then mark this step complete if you want to skip it.':
      'Sprawdź wartości ćwiczenia, a potem oznacz ten krok jako ukończony, jeśli chcesz go pominąć.',
    'This older exercise has invalid numeric values, so the workout view stays in a safe review mode.':
      'To starsze ćwiczenie ma nieprawidłowe wartości liczbowe, więc widok treningu pozostaje w bezpiecznym trybie przeglądu.',
    'Mark step complete': 'Zakończ krok',
    'Complete exercise': 'Ukończ ćwiczenie',
    'Rep Target': 'Cel powtórzeń',
    'Current Set': 'Aktualna seria',
    'Sets Left': 'Pozostałe serie',
    'Set {current} of {total}': 'Seria {current} z {total}',
    'Target: {count} reps': 'Cel: {count} powtórzeń',
    'Do {count} reps, then tap Done set.':
      'Wykonaj {count} powtórzeń, a potem kliknij Zakończ serię.',
    'Do {count} reps for set {current} of {total}, then tap Done set.':
      'Wykonaj {count} powtórzeń w serii {current} z {total}, a potem kliknij Zakończ serię.',
    'Complete set {current} of {total}, then tap Done set.':
      'Ukończ serię {current} z {total}, a potem kliknij Zakończ serię.',
    'Sets completed: {completed} / {total}':
      'Ukończone serie: {completed} / {total}',
    'Do {count} reps, then tap Complete exercise.':
      'Wykonaj {count} powtórzeń, a potem kliknij Ukończ ćwiczenie.',
    'Done set': 'Zakończ serię',
    Adjust: 'Dostosuj',
    'Adjust progress': 'Dostosuj postęp',
    Reset: 'Resetuj',
    'Rest Step': 'Krok przerwy',
    'Recovery Phase': 'Faza regeneracji',
    'Hold Timer': 'Timer trzymania',
    'Tempo Timer': 'Timer tempa',
    'Tempo phases': 'Fazy tempa',
    'Isometric Reps': 'Powtórzenia izometryczne',
    Recovery: 'Regeneracja',
    Ready: 'Gotowe',
    Tempo: 'Tempo',
    Isometric: 'Izometria',
    'Ready for tempo reps': 'Gotowe na powtórzenia w tempie',
    'Ready for isometric reps': 'Gotowe na powtórzenia izometryczne',
    'Breathing break. Recovery started.':
      'Przerwa na oddech. Regeneracja rozpoczęta.',
    'Set complete. Rest started.': 'Seria ukończona. Przerwa rozpoczęta.',
    'Use this recovery step to breathe, reset, and ease into what comes next.':
      'Wykorzystaj tę przerwę regeneracyjną, aby odetchnąć, wrócić do rytmu i spokojnie przejść dalej.',
    'Recover now, then get ready for the next set.':
      'Teraz odpocznij, a potem przygotuj się do kolejnej serii.',
    'Recover now': 'Teraz odpocznij',
    'Recovery time: {seconds}s': 'Czas regeneracji: {seconds}s',
    'Rest time: {seconds}s': 'Czas przerwy: {seconds}s',
    'Rest for {seconds}s, then start set {current} of {total}.':
      'Odpocznij {seconds}s, a potem zacznij serię {current} z {total}.',
    'Rest for {seconds}s, then continue.':
      'Odpocznij {seconds}s, a potem kontynuuj.',
    'Target: {seconds}s': 'Cel: {seconds}s',
    'Hold {side} for {seconds}s.': 'Trzymaj {side} przez {seconds}s.',
    'Hold for {seconds}s.': 'Trzymaj przez {seconds}s.',
    'Hold for {seconds}s to complete set {current} of {total}.':
      'Trzymaj przez {seconds}s, aby ukończyć serię {current} z {total}.',
    'reps each side': 'powtórzeń na stronę',
    'Complete both sides, then tap once.':
      'Wykonaj obie strony, a następnie naciśnij raz.',
    'Reps left: {remaining} / {total}':
      'Pozostałe powtórzenia: {remaining} / {total}',
    '{phase}: {seconds}s': '{phase}: {seconds}s',
    '{phase}: {seconds}s for rep {current} of {total}.':
      '{phase}: {seconds}s w powtórzeniu {current} z {total}.',
    'Start rep {current} of {total}.':
      'Rozpocznij powtórzenie {current} z {total}.',
    'Get ready for {phase}.': 'Przygotuj się: {phase}.',
    'Start the timer when you are ready.':
      'Uruchom timer, gdy możesz zaczynać.',
    'Start with a 3-second lead-in': 'Zacznij od 3-sekundowego przygotowania',
    'Each rep holds for {seconds}s and advances automatically.':
      'Każde powtórzenie trwa {seconds}s i automatycznie przechodzi dalej.',
    'Rep {current} of {total}': 'Powtórzenie {current} z {total}',
    'Finish the left tempo reps, then repeat on the right side.':
      'Dokończ powtórzenia w tempie po lewej stronie, potem powtórz po prawej.',
    'Finish the left isometric reps, then repeat on the right side.':
      'Dokończ powtórzenia izometryczne po lewej stronie, potem powtórz po prawej.',
    'Dedicated recovery step': 'Osobny krok regeneracyjny',
    'Next set: {current} of {total}': 'Następna seria: {current} z {total}',
    Pause: 'Pauza',
    Resume: 'Wznów',
    'Start reps': 'Zacznij powtórzenia',
    'Start recovery': 'Zacznij regenerację',
    'Start rest': 'Zacznij przerwę',
    'Skip rest': 'Pomiń przerwę',
    'Start next set': 'Zacznij kolejną serię',
    'Start set {current}': 'Zacznij serię {current}',
    'Start next rep': 'Zacznij kolejne powtórzenie',
    'Start hold': 'Zacznij trzymanie',
    'Skip hold': 'Pomiń trzymanie',
    'Start tempo': 'Zacznij tempo',
    'Start isometric reps': 'Zacznij powtórzenia izometryczne',
    'Start next tempo rep': 'Zacznij kolejne powtórzenie w tempie',
    'Start next isometric rep': 'Zacznij kolejne powtórzenie izometryczne',
    'Resume hold': 'Wznów trzymanie',
    'Resume tempo': 'Wznów tempo',
    'Resume isometric reps': 'Wznów powtórzenia izometryczne',
    'Sound cues': 'Sygnały dźwiękowe',
    'Cues on': 'Sygnały wł.',
    'Cues off': 'Sygnały wył.',
    'Turn sound cues on': 'Włącz sygnały dźwiękowe',
    'Turn sound cues off': 'Wyłącz sygnały dźwiękowe',
    'Sound cues are unavailable in this browser.':
      'Sygnały dźwiękowe są niedostępne w tej przeglądarce.',
    'Workout sounds': 'Dźwięki treningu',
    'Play quiet cues during timed workout phases.':
      'Odtwarzaj ciche sygnały podczas odmierzanych faz treningu.',
    'Set controls': 'Sterowanie serią',
    'Restart set': 'Zacznij serię od nowa',
    'Skip set': 'Pomiń serię',
    'Adjust timer': 'Dostosuj timer',
    'Reset timer': 'Resetuj timer',
    'Reduce timer by five seconds': 'Skróć timer o pięć sekund',
    'Increase timer by five seconds': 'Wydłuż timer o pięć sekund',
    'Guidance and support': 'Wskazówki i wsparcie',
    'Show demo and notes': 'Pokaż demo i notatki',
    'Show demo': 'Pokaż demo',
    'Show notes': 'Pokaż notatki',
    'Open the reference clip in YouTube without crowding the workout screen.':
      'Otwórz materiał referencyjny w YouTube bez przeładowywania ekranu treningu.',
    Play: 'Odtwórz',
    'Install Guide': 'Instrukcja instalacji',
    'Add Forma to your Home screen.': 'Dodaj Formę do ekranu głównego.',
    'Open Chrome’s ⋮ menu': 'Otwórz menu ⋮ w Chrome',
    'Tap Add to Home screen': 'Wybierz „Dodaj do ekranu głównego”',
    'Add to Home screen': 'Dodaj do ekranu głównego',
    'Confirm installation': 'Potwierdź instalację',
    'Steps may vary slightly by Chrome version.':
      'Poszczególne kroki mogą się nieco różnić w zależności od wersji Chrome.',

    'Follow these steps in Chrome to add Forma to your Android Home screen.':
      'Dodaj Formę do ekranu głównego Androida, korzystając z instrukcji dla Chrome.',
    'Open the Chrome menu': 'Otwórz menu Chrome',
    'Tap the three dots next to the address bar.':
      'Kliknij trzy kropki obok paska adresu.',
    'Find the install option': 'Znajdź opcję instalacji',
    'Choose “Install and create shortcut” or “Add to Home screen”, depending on your Chrome version.':
      'Wybierz „Zainstaluj i utwórz skrót” lub „Dodaj do ekranu głównego”, zależnie od wersji Chrome.',
    'Choose Install and confirm. Then open Forma from your Home screen or app drawer.':
      'Wybierz „Zainstaluj” i potwierdź. Następnie otwórz Formę z ekranu głównego lub listy aplikacji.',
    'Install and create shortcut': 'Zainstaluj i utwórz skrót',
    'For these steps, open this page in Chrome.':
      'Aby skorzystać z tej instrukcji, otwórz tę stronę w Chrome.',
    'Close guide': 'Zamknij instrukcję',
    'Don’t show again': 'Nie pokazuj ponownie',
    'Copy is not available in this browser. Open this page in Chrome to install Forma.':
      'Ta przeglądarka nie obsługuje kopiowania. Otwórz tę stronę w Chrome, aby zainstalować Formę.',
    'Current page link copied. Paste it into Chrome, then install Forma.':
      'Link do tej strony został skopiowany. Wklej go w Chrome i zainstaluj Formę.',
    'Could not copy the link. Try opening this page directly in Chrome.':
      'Nie udało się skopiować linku. Spróbuj otworzyć tę stronę bezpośrednio w Chrome.',
    'Keep the app in view while you add Forma to your iPhone Home Screen.':
      'Dodaj Formę do ekranu głównego iPhone’a, nie tracąc aplikacji z oczu.',
    'Enable temporary debug surfaces while running the app in dev mode.':
      'Włącz tymczasowe narzędzia debugujące podczas uruchamiania aplikacji w trybie dev.',
    'Install guide': 'Instrukcja instalacji',
    'Show the install prompt and its debug panel.':
      'Pokaż monit instalacji i jego panel debugowania.',
    'Toggle install guide': 'Przełącz instrukcję instalacji',
    'Firestore trace': 'Ślad Firestore',
    'Log labeled Firestore reads and listeners to the browser console.':
      'Loguj opisane odczyty i listenery Firestore w konsoli przeglądarki.',
    'Toggle Firestore trace': 'Przełącz ślad Firestore',
    'Render profiler': 'Profiler renderowania',
    'Log React render commits with route and duration details.':
      'Loguj commity renderowania Reacta z trasą i czasem trwania.',
    'Toggle render profiler': 'Przełącz profiler renderowania',
    'Trace results': 'Wyniki śladu',
    '{count} trace events ready to copy.':
      '{count} zdarzeń śladu gotowych do skopiowania.',
    'Copy trace': 'Kopiuj ślad',
    'Clear trace': 'Wyczyść ślad',
    'Trace copied.': 'Ślad skopiowany.',
    'Trace could not be copied.': 'Nie udało się skopiować śladu.',
    'Render results': 'Wyniki renderowania',
    '{count} render events ready to copy.':
      '{count} zdarzeń renderowania gotowych do skopiowania.',
    Firestore: 'Firestore',
    Renders: 'Rendery',
    Copy: 'Kopiuj',
    Clear: 'Wyczyść',
    Copied: 'Skopiowano',
    'Copy failed': 'Nie skopiowano',
    events: 'zdarzeń',
    'Copy renders': 'Kopiuj rendery',
    'Clear renders': 'Wyczyść rendery',
    'Render profile copied.': 'Profil renderowania skopiowany.',
    'Render profile could not be copied.':
      'Nie udało się skopiować profilu renderowania.',
    Push: 'Push',
    Toast: 'Toast',
    'App version': 'Wersja aplikacji',
    Version: 'Wersja',
    Built: 'Zbudowano',
    Deployment: 'Wdrożenie',
    'Local development build': 'Lokalna wersja deweloperska',
    Help: 'Pomoc',
    'Found a problem? Send us the details and we’ll take a look.':
      'Znalazłeś problem? Prześlij nam szczegóły, a my się temu przyjrzymy.',
    'Report a problem': 'Zgłoś problem',
    'Saving...': 'Zapisywanie...',
    'Forma bug report': 'Zgłoszenie błędu Forma',
    'Describe what happened:': 'Opisz, co się stało:',
    'Notification tests': 'Testy powiadomień',
    'Schedule a browser notification or a fake in-app reminder toast in 5 seconds.':
      'Zaplanuj powiadomienie przeglądarki albo testowy toast w aplikacji za 5 sekund.',
    'Schedule browser notification': 'Zaplanuj powiadomienie przeglądarki',
    'Schedule in-app toast': 'Zaplanuj toast w aplikacji',
    General: 'Ogólne',
    Intensity: 'Intensywność',
    Core: 'Core',
    Legs: 'Nogi',
    Strength: 'Siła',
    Cardio: 'Cardio',
    Conditioning: 'Kondycja',
    Mobility: 'Mobilność',
    'Create a Firebase web app in the Firebase console.':
      'Utwórz aplikację webową Firebase w konsoli Firebase.',
    'Enable Google in Authentication > Sign-in method.':
      'Włącz Google w Authentication > Sign-in method.',
    'Enable Anonymous in Authentication > Sign-in method.':
      'Włącz Anonymous w Authentication > Sign-in method.',
    'Enable Firestore Database in the Firebase console.':
      'Włącz Firestore Database w konsoli Firebase.',
    'Add the Firebase web config to apps/web/.env.local.':
      'Dodaj konfigurację Firebase web do apps/web/.env.local.',
    'Restart the Vite dev server after updating env vars.':
      'Uruchom ponownie serwer Vite po aktualizacji zmiennych środowiskowych.',
    Mon: 'Pon',
    Tue: 'Wt',
    Wed: 'Śr',
    Thu: 'Czw',
    Fri: 'Pt',
    Sat: 'Sob',
    Sun: 'Niedz',
    'Fix workout values': 'Popraw wartości treningu',
    'Simple step': 'Tylko do odhaczenia',
    '{count} reps': '{count} powtórzeń',
    '{count} tempo reps': '{count} powtórzeń w tempie',
    '{count} isometric reps': '{count} powtórzeń izometrycznych',
    '{seconds}s hold': '{seconds}s trzymania',
    '{sets} sets x {count} reps': '{sets} serie x {count} powtórzeń',
    '{sets} sets x {count} tempo reps':
      '{sets} serie x {count} powtórzeń w tempie',
    '{sets} sets x {count} isometric reps':
      '{sets} serie x {count} powtórzeń izometrycznych',
    '{sets} timed sets': '{sets} serie czasowe',
    '{count} timed reps': '{count} powtórzeń na czas',
    '{sets} sets x {count} timed reps':
      '{sets} serie x {count} powtórzeń na czas',
    '{seconds}s rest step': '{seconds}s kroku przerwy',
    '{sets} sets': '{sets} serie',
    'Use values above 0 or clear the field.':
      'Użyj wartości większych od 0 albo wyczyść pole.',
    'You can keep this exercise as a simple step. Add reps, time, sides, coaching, or a demo if you want more guidance.':
      'Możesz zostawić ćwiczenie jako pozycję do odhaczenia. Jeśli ma prowadzić dokładniej, dodaj powtórzenia, czas, obie strony, wskazówki albo demo.',
    'Do the target reps at your own pace, then tap Complete exercise once at the end.':
      'Wykonaj docelową liczbę powtórzeń we własnym tempie, a na końcu kliknij Ukończ ćwiczenie.',
    'Each rep follows the tempo phases before moving to the next rep.':
      'Każde powtórzenie ma fazy tempa, zanim przejdziesz do następnego.',
    'Each rep runs one isometric hold before moving to the next rep.':
      'Każde powtórzenie zawiera jedno trzymanie izometryczne przed przejściem do następnego.',
    'Run one hold timer and finish when it reaches zero.':
      'Uruchom jeden timer trzymania i zakończ, gdy dojdzie do zera.',
    'Do the target reps for each set, then tap Done set once at the end. A {seconds}s rest starts after each remaining set.':
      'Wykonaj docelową liczbę powtórzeń w każdej serii, a potem kliknij Zakończ serię. Po każdej pozostałej serii włączy się przerwa {seconds}s.',
    'Do the target reps for each set, then tap Done set once at the end.':
      'Wykonaj docelową liczbę powtórzeń w każdej serii, a potem kliknij Zakończ serię.',
    'Each rep follows the tempo phases. A {seconds}s rest starts after each remaining set.':
      'Każde powtórzenie ma fazy tempa. Po każdej pozostałej serii włączy się przerwa {seconds}s.',
    'Each rep follows the tempo phases across all sets.':
      'Każde powtórzenie ma fazy tempa we wszystkich seriach.',
    'Each rep runs one isometric hold. A {seconds}s rest starts after each remaining set.':
      'Każde powtórzenie zawiera jedno trzymanie izometryczne. Po każdej pozostałej serii włączy się przerwa {seconds}s.',
    'Each rep runs one isometric hold across all sets.':
      'Każde powtórzenie zawiera jedno trzymanie izometryczne we wszystkich seriach.',
    'Each set runs one hold timer. A {seconds}s rest runs between remaining sets.':
      'W każdej serii działa jeden timer trzymania. Między pozostałymi seriami trwa przerwa {seconds}s.',
    'Each set runs one hold timer until all sets are complete.':
      'W każdej serii działa jeden timer trzymania, aż wszystkie serie zostaną ukończone.',
    'Each rep runs the hold timer before moving to the next rep.':
      'W każdym powtórzeniu timer trzymania działa przed przejściem do kolejnego.',
    'Each rep runs the hold timer. A {seconds}s rest runs between remaining sets.':
      'W każdym powtórzeniu działa timer trzymania. Między pozostałymi seriami trwa przerwa {seconds}s.',
    'Each rep runs one isometric hold. A {seconds}s rest runs between remaining sets.':
      'Każde powtórzenie zawiera jedno trzymanie izometryczne. Między pozostałymi seriami trwa przerwa {seconds}s.',
    'Each rep runs the hold timer across all sets.':
      'W każdym powtórzeniu timer trzymania działa we wszystkich seriach.',
    'A dedicated recovery step with one rest timer. Use it for breathing, resetting, or easing into the next exercise.':
      'Osobna przerwa regeneracyjna z jednym timerem. Użyj jej na oddech, reset albo spokojne wejście w kolejne ćwiczenie.',
    'Track each set manually. Add reps or time if you want more guidance. A {seconds}s rest runs between remaining sets.':
      'Śledź każdą serię ręcznie. Dodaj powtórzenia albo czas, jeśli chcesz więcej wskazówek. Między pozostałymi seriami działa {seconds}s przerwy.',
    'Track each set manually. Add reps or time if you want more guidance.':
      'Śledź każdą serię ręcznie. Dodaj powtórzenia albo czas, jeśli chcesz więcej wskazówek.',
    'Zero is not a valid workout value.':
      'Zero nie jest prawidłową wartością treningową.',
    'Down/up timing is only used with reps.':
      'Czasy opuszczania i podnoszenia są używane tylko przy powtórzeniach.',
    'Tempo reps need both down and up timing.':
      'Powtórzenia w tempie wymagają czasu opuszczania i podnoszenia.',
    'Short hold reps need both down and up timing, or use at least 10s for isometric reps.':
      'Krótkie powtórzenia z trzymaniem wymagają czasu opuszczania i podnoszenia albo co najmniej 10 s dla powtórzeń izometrycznych.',
    'Rest is only used between sets. Add sets to make this rest timer active.':
      'Przerwa działa tylko między seriami. Dodaj serie, aby aktywować timer przerwy.',
    'Get ready': 'Przygotuj się',
    Lower: 'Opuść',
    Hold: 'Trzymaj',
    Lift: 'Unieś',
    'Tempo set': 'Seria w tempie',
    '{seconds}s rest': '{seconds}s przerwy',
    'Every day': 'Codziennie',
    'Every {minutes} min': 'Co {minutes} min',
    '{time} • {days}': '{time} • {days}',
    '{interval}{window} • {days}': '{interval}{window} • {days}',
    'Reminder: {label}': 'Przypomnienie: {label}',
    'It is time for {label}': 'Czas na {label}',
    'Something went wrong. Please try again.':
      'Coś poszło nie tak. Spróbuj ponownie.',
    'The sign-in window was closed before the login finished.':
      'Okno logowania zostało zamknięte przed zakończeniem logowania.',
    'The browser blocked the popup, so we are switching to a full-page redirect.':
      'Przeglądarka zablokowała wyskakujące okno, więc przełączamy się na przekierowanie na pełnej stronie.',
    'Another sign-in window is already open.':
      'Inne okno logowania jest już otwarte.',
    'That email is already tied to another provider in Firebase.':
      'Ten adres e-mail jest już powiązany z innym dostawcą w Firebase.',
    'That Google account is already connected to another account.':
      'To konto Google jest już połączone z innym kontem.',
    'That sign-in method is already connected to another account.':
      'Ta metoda logowania jest już połączona z innym kontem.',
    'An account already exists for this email address.':
      'Konto z tym adresem e-mail już istnieje.',
    'Enter a valid email address.': 'Podaj prawidłowy adres e-mail.',
    'The email or password is incorrect.':
      'Adres e-mail lub hasło są nieprawidłowe.',
    'Use a password with at least six characters.':
      'Użyj hasła składającego się z co najmniej sześciu znaków.',
    'Google sign-in is not enabled for this Firebase project yet.':
      'Logowanie przez Google nie jest jeszcze włączone dla tego projektu Firebase.',
    'Apple sign-in is not fully configured in Firebase. Check the Service ID and OAuth code flow settings.':
      'Logowanie przez Apple nie jest w pełni skonfigurowane w Firebase. Sprawdź identyfikator usługi i ustawienia przepływu kodu OAuth.',
    'Email sign-in is not enabled for this Firebase project yet.':
      'Logowanie przez e-mail nie jest jeszcze włączone dla tego projektu Firebase.',
    'Anonymous sign-in is not enabled for this Firebase project yet.':
      'Logowanie anonimowe nie jest jeszcze włączone dla tego projektu Firebase.',
    'This domain is not authorized in Firebase Authentication.':
      'Ta domena nie jest autoryzowana w Firebase Authentication.',
    'Firebase returned an unexpected auth error. Please try again.':
      'Firebase zwrócił nieoczekiwany błąd autoryzacji. Spróbuj ponownie.',
    'Firestore returned an unexpected error.':
      'Firestore zwrócił nieoczekiwany błąd.',
    'Firestore denied the request. Check your Firestore security rules.':
      'Firestore odrzucił żądanie. Sprawdź reguły bezpieczeństwa Firestore.',
    'Firestore is not fully set up yet. Check whether the database is enabled.':
      'Firestore nie jest jeszcze w pełni skonfigurowany. Sprawdź, czy baza danych jest włączona.',
    'Firestore is temporarily unavailable.':
      'Firestore jest tymczasowo niedostępny.',
    'Firestore error: {code}': 'Błąd Firestore: {code}',
    'Routine creation failed.': 'Nie udało się utworzyć planu.',
    'Sign in before creating routines.': 'Zaloguj się przed tworzeniem planów.',
    'The routine details are invalid. Check the name and icon.':
      'Szczegóły planu są nieprawidłowe. Sprawdź nazwę i ikonę.',
    'That routine could not be found anymore.':
      'Nie można już znaleźć tego planu.',
    'That routine is not available to import.':
      'Ten plan nie jest dostępny do importu.',
    'Creating routines requires active trainer access.':
      'Tworzenie planów wymaga aktywnego dostępu trenera.',
    'Name shown to clients': 'Nazwa widoczna dla klientów',
    'Clients see it on your invites and in their app.':
      'Klienci zobaczą ją w zaproszeniach i w swojej aplikacji.',
    'Use {count} characters or fewer.': 'Użyj maksymalnie {count} znaków.',
    'Remove special characters from the name.': 'Usuń znaki specjalne z nazwy.',
    'Your name was not saved. Try again.':
      'Nie udało się zapisać nazwy. Spróbuj ponownie.',
    'Name saved.': 'Nazwa zapisana.',
    'The routine service is temporarily unavailable.':
      'Usługa planów jest tymczasowo niedostępna.',
    'Routine service error: {code}': 'Błąd usługi planów: {code}',
    Connected: 'Połączono',
    'Allowed, not connected': 'Dozwolone, niepołączone',
    Blocked: 'Zablokowane',
    Unavailable: 'Niedostępne',
    Unsupported: 'Nieobsługiwane',
    'This device can receive reminder notifications.':
      'To urządzenie może otrzymywać powiadomienia o przypomnieniach.',
    'Connect this device so reminders can reach it.':
      'Połącz to urządzenie, aby przypomnienia mogły do niego docierać.',
    'Enable notifications in your browser settings to receive reminders on this device.':
      'Włącz powiadomienia w ustawieniach przeglądarki, aby otrzymywać przypomnienia na tym urządzeniu.',
    'Notifications are not configured for this app environment yet.':
      'Powiadomienia nie są jeszcze skonfigurowane dla tego środowiska aplikacji.',
    'This browser or context does not support push notifications.':
      'Ta przeglądarka lub kontekst nie obsługuje powiadomień push.',
    'Turn on notifications when you want reminders to reach this device.':
      'Włącz powiadomienia, gdy chcesz otrzymywać przypomnienia na tym urządzeniu.',
    'Use a value above 0 for {label}, or clear the field.':
      'Użyj wartości większej od 0 dla pola {label} albo wyczyść to pole.',
    'Push permission is granted, but device registration failed.':
      'Uprawnienie push zostało przyznane, ale rejestracja urządzenia się nie powiodła.',
    'This browser cannot receive push notifications. The reminder will still be saved.':
      'Ta przeglądarka nie może odbierać powiadomień push. Przypomnienie i tak zostanie zapisane.',
    'Notifications are not configured in this app environment yet. The reminder will still be saved.':
      'Powiadomienia nie są jeszcze skonfigurowane w tym środowisku aplikacji. Przypomnienie i tak zostanie zapisane.',
    'Notifications are blocked on this device. Enable them in browser settings when you are ready.':
      'Powiadomienia są zablokowane na tym urządzeniu. Włącz je w ustawieniach przeglądarki, gdy zechcesz.',
    'Notifications are enabled for this device.':
      'Powiadomienia są włączone na tym urządzeniu.',
    'Notifications are connected for this device.':
      'Powiadomienia są połączone na tym urządzeniu.',
    'Notifications are allowed, but this device is not connected yet.':
      'Powiadomienia są dozwolone, ale to urządzenie nie jest jeszcze połączone.',
    'Reminder saved, but notifications are still off for this device.':
      'Przypomnienie zapisane, ale powiadomienia nadal są wyłączone na tym urządzeniu.',
    'Turn on notifications to receive active reminders on this device.':
      'Włącz powiadomienia, aby otrzymywać aktywne przypomnienia na tym urządzeniu.',
    'Sign in before enabling push notifications.':
      'Zaloguj się przed włączeniem powiadomień push.',
    'Developer reminder test': 'Test przypomnienia deweloperskiego',
    'Browser notification permission is still off.':
      'Uprawnienie do powiadomień przeglądarki nadal jest wyłączone.',
    'Browser notification sent.': 'Powiadomienie przeglądarki zostało wysłane.',
    'Browser notification test failed.':
      'Test powiadomienia przeglądarki nie powiódł się.',
    'Browser notification scheduled in {seconds}s.':
      'Powiadomienie przeglądarki zaplanowano za {seconds}s.',
    'In-app reminder toast scheduled in {seconds}s.':
      'Toast przypomnienia w aplikacji zaplanowano za {seconds}s.',
    'Copy is not available in this browser. Open this page in Safari to install Forma.':
      'Kopiowanie nie jest dostępne w tej przeglądarce. Otwórz tę stronę w Safari, aby zainstalować Formę.',
    'Current page link copied. Paste it into Safari, then add Forma to your Home Screen.':
      'Link do bieżącej strony został skopiowany. Wklej go w Safari, a potem dodaj Formę do ekranu głównego.',
    'Could not copy the link. Try opening this page directly in Safari.':
      'Nie udało się skopiować linku. Spróbuj otworzyć tę stronę bezpośrednio w Safari.',
    'Sign in before saving reminders.':
      'Zaloguj się przed zapisaniem przypomnień.',
    'Choose a routine for this reminder.':
      'Wybierz plan dla tego przypomnienia.',
    '{name} reminder': 'Przypomnienie: {name}',
    'Desk mobility': 'Mobilność przy biurku',
    'Choose a time for the daily reminder.':
      'Wybierz godzinę dla codziennego przypomnienia.',
    'Add a valid interval in minutes.': 'Podaj prawidłowy odstęp w minutach.',
    'Pick at least one day for the reminder.':
      'Wybierz co najmniej jeden dzień dla przypomnienia.',
    Schedule: 'Harmonogram',
    'Schedule time': 'Godzina harmonogramu',
    'Schedule times': 'Godziny harmonogramu',
    'Schedule time {number}': 'Godzina harmonogramu {number}',
    'Add time': 'Dodaj godzinę',
    'Remove schedule time': 'Usuń godzinę harmonogramu',
    'Remove schedule time {number}': 'Usuń godzinę harmonogramu {number}',
    'Up to {count} times a day.': 'Maksymalnie {count} godziny dziennie.',
    'Client: {client}': 'Klient: {client}',
    'Editing changes one reminder. Add a new reminder for multiple times.':
      'Edycja zmienia jedno przypomnienie. Dodaj nowe przypomnienie, aby ustawić wiele godzin.',
    'Edit assignment': 'Edytuj przypisanie',
    'Save assignment': 'Zapisz przypisanie',
    'Assignment updated.': 'Przypisanie zaktualizowane.',
    'Assignment type': 'Rodzaj przypisania',
    '1-on-1 session': 'Sesja 1 na 1',
    Homework: 'Praca własna',
    'You meet the client at this time. A schedule is required.':
      'Spotykasz się z klientem w tym czasie. Harmonogram jest wymagany.',
    'The client works on their own. A schedule is optional.':
      'Klient pracuje samodzielnie. Harmonogram jest opcjonalny.',
    'When should it happen?': 'Kiedy ma się odbywać?',
    Anytime: 'Kiedykolwiek',
    'At set times': 'W ustalonych godzinach',
    'The client can do this whenever they want. It stays out of adherence tracking.':
      'Klient może to zrobić, kiedy chce. Nie jest liczone do realizacji planu.',
    'Sessions repeat weekly. Move a single week from the schedule below.':
      'Sesje powtarzają się co tydzień. Pojedynczy tydzień przenieś w harmonogramie poniżej.',
    'Move this session': 'Przenieś tę sesję',
    'Move {routine} on {date}': 'Przenieś {routine} w {date}',
    '{routine} on {date}. Only this one week changes.':
      '{routine} w {date}. Zmienia się tylko ten tydzień.',
    'Only this one week changes.': 'Zmienia się tylko ten tydzień.',
    'New time': 'Nowa godzina',
    'The weekly slot stays at {time}.':
      'Cotygodniowy termin pozostaje o {time}.',
    'Cancel this week': 'Odwołaj w tym tygodniu',
    'Back to the weekly time': 'Wróć do cotygodniowej godziny',
    'Save new time': 'Zapisz nową godzinę',
    'moved from {time}': 'przeniesione z {time}',
    'Off today': 'Dziś wolne',
    '{day}: your trainer moved this session to {time}.':
      '{day}: trener przeniósł tę sesję na {time}.',
    '{day}: your trainer cancelled this session.':
      '{day}: trener odwołał tę sesję.',
    'Moved to {time}': 'Przeniesione na {time}',
    'Session moved to {time}.': 'Sesja przeniesiona na {time}.',
    'Session cancelled for that week.': 'Sesja odwołana w tym tygodniu.',
    'Session back on its weekly time.':
      'Sesja wróciła na swoją cotygodniową godzinę.',
    'Moving sessions is not available right now.':
      'Przenoszenie sesji jest teraz niedostępne.',
    'Choose how and when this routine is expected.':
      'Wybierz, jak i kiedy ten plan ma być wykonywany.',
    'Homework with no set time. Not counted in adherence.':
      'Praca własna bez ustalonej godziny. Nie liczy się do realizacji planu.',
    'This client only has anytime homework. Add a session or set times to fill the week.':
      'Ten klient ma tylko pracę własną bez godzin. Dodaj sesję lub ustal godziny, aby wypełnić tydzień.',
    'Pick at least one schedule day.':
      'Wybierz co najmniej jeden dzień harmonogramu.',
    'Choose a schedule time.': 'Wybierz godzinę harmonogramu.',
    'Choose one to four schedule times.':
      'Wybierz od jednej do czterech godzin harmonogramu.',
    'On track': 'Zgodnie z planem',
    'Due today': 'Zaplanowane na dziś',
    'Needs check-in': 'Wymaga sprawdzenia',
    'In progress': 'W trakcie',
    'Clients ({count})': 'Klienci ({count})',
    'Routines ({count})': 'Plany ({count})',
    'Exercises ({count})': 'Ćwiczenia ({count})',
    Exercises: 'Ćwiczenia',
    'Review reusable exercises before adding them to routines.':
      'Przejrzyj ćwiczenia do ponownego użycia, zanim dodasz je do planów.',
    'Create private exercises once, then reuse them across routines.':
      'Twórz prywatne ćwiczenia raz, a potem używaj ich ponownie w różnych planach.',
    'Add client': 'Dodaj klienta',
    'Clients could not load.': 'Nie udało się wczytać klientów.',
    'Clients could not load. Try again to continue.':
      'Nie udało się wczytać klientów. Spróbuj ponownie, aby kontynuować.',
    'Try again before reviewing client profiles.':
      'Spróbuj ponownie, zanim przejrzysz profile klientów.',
    'Client activity is unavailable. Client profiles are still available.':
      'Aktywność klientów jest niedostępna. Nadal możesz otwierać ich profile.',
    'Client activity is still loading. You can open clients now.':
      'Aktywność klientów nadal się wczytuje. Możesz już otwierać ich profile.',
    'Loading client activity…': 'Wczytywanie aktywności klienta…',
    'Client activity unavailable': 'Aktywność klienta niedostępna',
    'Adding client...': 'Dodawanie klienta...',
    'Client invite ready': 'Zaproszenie dla klienta jest gotowe',
    'Client already connected': 'Klient ma już połączenie',
    'Ask your client to scan this code with their phone. This private link works until they use it to verify.':
      'Poproś klienta o zeskanowanie tego kodu telefonem. Ten prywatny link działa, dopóki klient nie użyje go do weryfikacji.',
    'Invitation QR code': 'Kod QR zaproszenia',
    'Scan to open the Forma invitation':
      'Zeskanuj, aby otworzyć zaproszenie do Formy',
    'Invitation link': 'Link do zaproszenia',
    'Link copied': 'Link skopiowany',
    'Client added and their invite link copied.':
      'Klient został dodany, a link z zaproszeniem skopiowany.',
    'Client added. Share their invite link to connect.':
      'Klient został dodany. Udostępnij link z zaproszeniem, aby się połączyć.',
    'Add routine': 'Dodaj plan',
    'Routines could not load. Try again to continue.':
      'Nie udało się wczytać planów. Spróbuj ponownie, aby kontynuować.',
    'Exercises could not load. Try again to continue.':
      'Nie udało się wczytać ćwiczeń. Spróbuj ponownie, aby kontynuować.',
    'Try again before adding or assigning client plans.':
      'Spróbuj ponownie, zanim dodasz lub przypiszesz plany klientom.',
    'Assignment counts are unavailable. You can still manage routines.':
      'Liczba przypisań jest niedostępna. Nadal możesz zarządzać planami.',
    'Assignment counts are still loading. You can manage routines now.':
      'Liczba przypisań nadal się wczytuje. Możesz już zarządzać planami.',
    'Loading assignment counts…': 'Wczytywanie liczby przypisań…',
    'Assignment counts unavailable': 'Liczba przypisań niedostępna',
    'No routines yet': 'Brak planów treningowych',
    'Start with a Forma routine or create a blank plan. You can assign it from a client profile once it is ready.':
      'Zacznij od planu Formy albo utwórz pusty plan. Gdy będzie gotowy, możesz przypisać go w profilu klienta.',
    'Only routine': 'Jedyny plan',
    'This routine is ready to edit, duplicate, or assign from a client profile.':
      'Ten plan jest gotowy do edycji, zduplikowania albo przypisania z profilu klienta.',
    'Review who completed their routine, who is due, and who needs a check-in.':
      'Sprawdź, kto wykonał plan, kto ma go dziś w harmonogramie i kto wymaga kontaktu.',
    'Search and manage material ready for assignments.':
      'Wyszukuj plany i ćwiczenia gotowe do przypisania.',
    'Search clients': 'Szukaj klientów',
    'Search by name or email': 'Szukaj po nazwie lub adresie e-mail',
    'Filter clients': 'Filtruj klientów',
    'All clients': 'Wszyscy klienci',
    'Filter by body area': 'Filtruj według obszaru ciała',
    'All body areas': 'Wszystkie obszary ciała',
    Uncategorized: 'Bez kategorii',
    'Sort results': 'Sortuj wyniki',
    'Priority first': 'Najpierw priorytetowe',
    Name: 'Nazwa',
    'Recently updated': 'Ostatnio zaktualizowane',
    'No clients match this search or filter.':
      'Żaden klient nie pasuje do wyszukiwania lub filtra.',
    'No routines match this search.': 'Żaden plan nie pasuje do wyszukiwania.',
    'Assigned to {count}': 'Przypisano klientom: {count}',
    'Assigned routines: {count}': 'Przypisane plany: {count}',
    'Not assigned': 'Nieprzypisana',
    Pagination: 'Stronicowanie',
    'Page {page} of {total}': 'Strona {page} z {total}',
    'Page {page}': 'Strona {page}',
    'Previous page': 'Poprzednia strona',
    Previous: 'Poprzednia',
    'Next page': 'Następna strona',
    Next: 'Następna',
    'Scheduled: {count}': 'Zaplanowane: {count}',
    'On track: {count}': 'Zgodnie z planem: {count}',
    'Due today: {count}': 'Na dziś: {count}',
    'Needs check-in: {count}': 'Do sprawdzenia: {count}',
    'Scheduled later': 'Zaplanowane później',
    'Scheduled workout missed': 'Pominięto zaplanowany trening',
    'Trainer schedule': 'Harmonogram trenera',
    'Create reminder from trainer schedule':
      'Utwórz przypomnienie z harmonogramu trenera',
    'Prefilled with the first trainer schedule time. Add another reminder for each extra time.':
      'Wypełniono pierwszą godziną z harmonogramu trenera. Dodaj osobne przypomnienie dla każdej kolejnej godziny.',
    'Schedule assignment': 'Zaplanuj przypisanie',
    '{completed} / {expected} expected in 7 days':
      '{completed} / {expected} w planie na 7 dni',
    '{count} in 7 days': 'W planie na 7 dni: {count}',
    'Assign a routine to start tracking progress.':
      'Przypisz plan, aby zacząć śledzić postęp.',
    '{completed} of {expected} completed in the last 7 days':
      'Wykonano {completed} z {expected} w ostatnich 7 dniach',
    '{count} completed in the last 7 days':
      'Wykonano w ostatnich 7 dniach: {count}',
    'Due from {day}': 'Do wykonania od {day}',
    'Missed routine': 'Pominięty plan',
    'No schedule': 'Brak harmonogramu',
    'No completion logged in the last 7 days':
      'Brak ukończonych treningów w ostatnich 7 dniach',
    'Scheduled today': 'Zaplanowane dzisiaj',
    'Weekly rhythm: {days}': 'Rytm tygodnia: {days}',
    '{day}: completed': '{day}: wykonano',
    '{day}: completed today': '{day}: wykonano dzisiaj',
    '{day}: missed': '{day}: pominięto',
    '{day}: missed today': '{day}: pominięto dzisiaj',
    '{day}: scheduled': '{day}: zaplanowano',
    '{day}: scheduled today': '{day}: zaplanowano dzisiaj',
    '{day}: no scheduled routine': '{day}: brak planu na ten dzień',
    '{day}: no scheduled routine today': '{day}: brak planu na dzisiaj',
    'Weekly schedule for {client}': 'Harmonogram tygodnia dla {client}',
    'Browse planned, completed, and missed work by week.':
      'Przeglądaj zaplanowane, wykonane i pominięte treningi według tygodni.',
    'Show previous week': 'Pokaż poprzedni tydzień',
    'Show next week': 'Pokaż następny tydzień',
    'This week': 'Ten tydzień',
    'Last week': 'Poprzedni tydzień',
    'Next week': 'Następny tydzień',
    '{count} weeks ago': '{count} tyg. temu',
    'In {count} weeks': 'Za {count} tyg.',
    '{relative}, week of {range}': '{relative}, tydzień {range}',
    'Rest day': 'Dzień odpoczynku',
    'Unnamed client': 'Klient bez nazwy',
    Now: 'Teraz',
    'No sessions scheduled': 'Brak zaplanowanych sesji',
    'Loading the day’s sessions…': 'Wczytywanie sesji z tego dnia…',
    'Showing {weekday}, {date}: {count} session(s)':
      'Pokazuję {weekday}, {date}: liczba sesji: {count}',
    "The day's sessions couldn't load. Try again in a moment.":
      'Nie udało się wczytać sesji z tego dnia. Spróbuj ponownie za chwilę.',
    "The day's sessions couldn't load.":
      'Nie udało się wczytać sesji z tego dnia.',
    'Try again before reviewing today’s schedule.':
      'Spróbuj ponownie, zanim przejrzysz dzisiejszy harmonogram.',
    'Review clients': 'Sprawdź klientów',
    Calendar: 'Kalendarz',
    'See who trains today and across the week, hour by hour.':
      'Zobacz, kto trenuje dziś i przez cały tydzień, godzina po godzinie.',
    'Day calendar': 'Kalendarz dzienny',
    'Show previous day': 'Pokaż poprzedni dzień',
    'Show next day': 'Pokaż następny dzień',
    'Jump to date': 'Przejdź do daty',
    'Nearby days': 'Sąsiednie dni',
    'Mark done': 'Oznacz jako wykonane',
    'Showing {weekday}, {date}': 'Pokazuję {weekday}, {date}',
    "This day's sessions will appear here.":
      'Sesje z tego dnia pojawią się tutaj.',
    'Step through the days to see who trains and when across all your clients.':
      'Przełączaj dni, aby sprawdzić, kto ma trening i o której godzinie.',
    'No schedule yet': 'Brak harmonogramu',
    'Assign a routine with a schedule to see this client’s week take shape.':
      'Przypisz plan z harmonogramem, aby zobaczyć tydzień klienta.',
    Completed: 'Wykonano',
    'Completed {time}': 'Wykonano {time}',
    'Mark {routine} for {name} as done':
      'Oznacz „{routine}” dla {name} jako wykonane',
    'Mark {routine} for {name} as not done':
      'Oznacz „{routine}” dla {name} jako niewykonane',
    'Marked {routine} as done for {name}.':
      'Oznaczono „{routine}” jako wykonane dla {name}.',
    'Marked {routine} as not done for {name}.':
      'Oznaczono „{routine}” jako niewykonane dla {name}.',
    'Marking sessions done is not available right now.':
      'Tymczasowo nie można oznaczać sesji jako wykonanych.',
    Missed: 'Pominięto',
    Due: 'Do wykonania',
    Upcoming: 'Nadchodzące',
    'No data': 'Brak danych',
    '{label} {time}': '{label} {time}',
    '{count} reminders created.': 'Utworzono przypomnienia: {count}.',
    'Reminder updated.': 'Przypomnienie zaktualizowane.',
    'Reminder created.': 'Przypomnienie utworzone.',
    'Turn on reminders for this schedule?':
      'Włączyć przypomnienia dla tego harmonogramu?',
    'Your trainer scheduled {routine} for you. Forma can remind you at those times.':
      'Twój trener zaplanował dla Ciebie {routine}. Forma może przypominać Ci o tych godzinach.',
    'You can change or delete these reminders at any time.':
      'Możesz zmienić lub usunąć te przypomnienia w dowolnym momencie.',
    'Turn on reminders': 'Włącz przypomnienia',
    'Turning on...': 'Włączanie...',
    'Reminders saved. Turn on notifications to receive them here.':
      'Przypomnienia zapisane. Włącz powiadomienia, aby otrzymywać je na tym urządzeniu.',
    'Sign in before deleting reminders.':
      'Zaloguj się przed usuwaniem przypomnień.',
    'Delete "{name}"?': 'Usunąć „{name}”?',
    'Delete reminder?': 'Usunąć przypomnienie?',
    'Delete "{name}"? This stops future notifications.':
      'Usunąć „{name}”? To zatrzyma przyszłe powiadomienia.',
    'this reminder': 'to przypomnienie',
    'Reminder deleted.': 'Przypomnienie usunięte.',
    'Workout finished, but activity history could not be saved.':
      'Trening zakończony, ale nie udało się zapisać historii aktywności.',
    'Excellent work': 'Świetna robota',
    'How did it feel?': 'Jak poszło?',
    'Share a quick note for {routine}.':
      'Dodaj krótką notatkę do planu {routine}.',
    'this routine': 'tego planu',
    Easy: 'Łatwo',
    Good: 'Dobrze',
    Hard: 'Trudno',
    'Optional note': 'Opcjonalna notatka',
    'Anything your trainer should know?': 'Czy trener powinien coś wiedzieć?',
    Skip: 'Pomiń',
    'Sending...': 'Wysyłanie...',
    'Send feedback': 'Wyślij opinię',
    'Feedback sent to your trainer.': 'Opinia wysłana do trenera.',
    'Feedback could not be sent.': 'Nie udało się wysłać opinii.',
    'Latest feedback: {rating}': 'Ostatnia opinia: {rating}',
    'Latest feedback: {rating} · {note}': 'Ostatnia opinia: {rating} · {note}',
    History: 'Historia',
    'History for {client}': 'Historia: {client}',
    'Sessions from routines you assigned.':
      'Sesje z planów, które przypisano klientowi.',
    'Loading history...': 'Wczytywanie historii...',
    '{client} has no completed sessions yet. Assigned routines they finish appear here.':
      '{client} nie ma jeszcze ukończonych sesji. Ukończone przypisane plany pojawią się tutaj.',
    'Marked done by you': 'Oznaczone przez Ciebie jako wykonane',
    'History could not be loaded.': 'Nie udało się wczytać historii.',
    'Show older sessions': 'Pokaż starsze sesje',
    'Show sets': 'Pokaż serie',
    'Hide sets': 'Ukryj serie',
    'Loading sets...': 'Wczytywanie serii...',
    'Sets could not be loaded.': 'Nie udało się wczytać serii.',
    'No sets with a weight were logged in this session.':
      'W tej sesji nie zapisano serii z ciężarem.',
    'No sets with a weight were logged for this exercise.':
      'Dla tego ćwiczenia nie zapisano serii z ciężarem.',
    Trend: 'Trend',
    'Show {exercise} trend': 'Pokaż trend: {exercise}',
    'Sets from routines you assigned, newest first.':
      'Serie z przypisanych przez Ciebie planów, od najnowszych.',
    'A trend appears after two sessions with a weight.':
      'Trend pojawi się po dwóch sesjach z ciężarem.',
    'Heaviest set went from {from} {unit} on {start} to {to} {unit} on {end}.':
      'Najcięższa seria zmieniła się z {from} {unit} ({start}) na {to} {unit} ({end}).',
    'Heaviest completed set per session, in {unit}.':
      'Najcięższa ukończona seria w każdej sesji, w {unit}.',
    'Latest target: {target} {unit}.': 'Ostatni cel: {target} {unit}.',
    'Solid line: heaviest completed set per session. Dashed line: target. In {unit}.':
      'Linia ciągła: najcięższa ukończona seria w sesji. Linia przerywana: cel. W {unit}.',
    'Unassign routine?': 'Odpiąć plan?',
    'The client keeps their account, but this routine will no longer appear as assigned trainer work.':
      'Klient zachowa konto, ale ten plan nie będzie już widoczny jako zadanie od trenera.',
    'Unassigning...': 'Odłączanie...',
    'Unassign routine': 'Odepnij plan',
    'Archive client': 'Archiwizuj klienta',
    'Archiving client...': 'Archiwizowanie klienta...',
    'Archive {client}?': 'Zarchiwizować klienta {client}?',
    'This frees one client slot and archives the client’s active routine assignments. You can reconnect later with a new invite.':
      'To zwolni jedno miejsce i zarchiwizuje aktywne przypisania planów klienta. Później możesz połączyć się ponownie przez nowe zaproszenie.',
    'Archived {client} and freed one client slot.':
      'Zarchiwizowano klienta {client} i zwolniono jedno miejsce.',
    Select: 'Wybierz',
    'Remove client': 'Usuń klienta',
    'Removing client...': 'Usuwanie klienta...',
    'Remove {client}?': 'Usunąć klienta {client}?',
    'This client hasn’t verified yet. Removing them deletes their prepared assignments, disables their invite link, and frees one client slot.':
      'Ten klient jeszcze nie zweryfikował konta. Usunięcie skasuje przygotowane przypisania, unieważni link z zaproszeniem i zwolni jedno miejsce.',
    'Removed {client} and freed one client slot.':
      'Usunięto klienta {client} i zwolniono jedno miejsce.',
    'Trainer client service is not available.':
      'Obsługa klientów trenera jest niedostępna.',
    'This client connection is not active.':
      'To połączenie z klientem nie jest aktywne.',
    'Thanks for using Forma': 'Dziękujemy za korzystanie z Formy',
    'Your trainer workspace is active and ready for clients.':
      'Twój panel trenera jest aktywny i gotowy dla klientów.',
    'Your trainer workspace is active and ready for clients. Thanks for helping shape Forma.':
      'Twój panel trenera jest aktywny i gotowy dla klientów. Dziękujemy, że pomagasz rozwijać Formę.',
    'Workspace demo': 'Demo panelu',
    'Demo workspace': 'Wersja demo',
    'Explore sample clients and routines. Changes are not saved.':
      'Poznaj przykładowych klientów i plany. Zmiany nie są zapisywane.',
    'Explore sample profiles and routines before choosing a plan. Nothing in the demo is saved.':
      'Poznaj przykładowe profile i plany przed wyborem abonamentu. Żadne zmiany w wersji demo nie są zapisywane.',
    'Explore demo': 'Zobacz demo',
    'Workspace guide': 'Przewodnik po panelu',
    'See how Forma supports the full plan workflow':
      'Zobacz, jak Forma wspiera cały proces pracy z planem',
    'Take a quick tour from building a routine and schedule to reviewing completion.':
      'Poznaj proces od tworzenia planu i harmonogramu po sprawdzenie realizacji.',
    'Open the workspace guide': 'Otwórz przewodnik po panelu',
    'See how it works': 'Zobacz, jak to działa',
    'Create clear exercise plans': 'Twórz przejrzyste plany ćwiczeń',
    'Assign plans and schedules': 'Przypisuj plany i harmonogramy',
    'Review plan completion': 'Sprawdzaj realizację planu',
    'Step {number}': 'Krok {number}',
    'Trainer billing': 'Płatności trenera',
    'Upgrade when you are ready to work with more clients.':
      'Przejdź na wyższy abonament, gdy chcesz pracować z większą liczbą klientów.',
    'Current plan': 'Obecny abonament',
    Monthly: 'Miesięcznie',
    Yearly: 'Rocznie',
    'Save about 15%': 'Oszczędzasz około 15%',
    'Coming soon': 'Wkrótce',
    'Download on the App Store': 'Pobierz z App Store',
    'Forma for iPhone': 'Forma na iPhone’a',
    'Your workout, on iPhone.': 'Twój trening na iPhonie.',
    'Expand preview': 'Powiększ podgląd',
    'Loading video…': 'Wczytywanie filmu…',
    'Video could not load. Try again.':
      'Nie udało się wczytać filmu. Spróbuj ponownie.',
    Retry: 'Spróbuj ponownie',
    'Forma iPhone app preview': 'Podgląd aplikacji Forma na iPhone’a',
    'Available soon': 'Dostępne wkrótce',
    'Trainer payments are on the way': 'Płatności dla trenerów już wkrótce',
    'Payments are not available in the trainer workspace yet. You can keep exploring Forma while we get them ready.':
      'Płatności nie są jeszcze dostępne w panelu trenera. Możesz dalej poznawać Formę, a my przygotujemy je do uruchomienia.',
    'Client capacity needs attention': 'Limit klientów wymaga uwagi',
    'You have {count} active clients, but this workspace currently allows {limit}. Archive {overBy} to continue. Capacity changes will be available soon.':
      'Masz {count} aktywnych klientów, a ten panel pozwala obecnie na {limit}. Zarchiwizuj {overBy}, aby kontynuować. Zmiana limitu będzie dostępna wkrótce.',
    'Manage your Forma Plus subscription before starting a trainer plan.':
      'Zarządzaj subskrypcją Forma Plus przed uruchomieniem abonamentu trenerskiego.',
    'Your trainer plan already includes Forma Plus. Manage that subscription instead.':
      'Twój abonament trenerski zawiera już Forma Plus. Zarządzaj nim zamiast osobną subskrypcją Forma Plus.',
    'This billing action is not available yet.':
      'Ta operacja rozliczeniowa nie jest jeszcze dostępna.',
    'Best choice': 'Najlepszy wybór',
    'Up to {count} clients': 'Do {count} klientów',
    'More than 50 clients?': 'Więcej niż 50 klientów?',
    'Tell us what your practice needs. We will shape a personal client limit and plan with you.':
      'Opowiedz nam, czego potrzebuje Twoja praktyka. Wspólnie dopasujemy indywidualny limit klientów i abonament.',
    'Contact us': 'Skontaktuj się',
    'Forma custom client limit': 'Forma — indywidualny limit klientów',
    'Tell us roughly how many active clients you need and how you work.':
      'Napisz, ilu aktywnych klientów potrzebujesz i jak pracujesz.',
    '{count} AI routine drafts per week':
      'Szkice planów od AI: {count} tygodniowo',
    'Included AI drafts renew on a rolling 7-day basis. Need more in the same week? You can buy a pack of {count} extra drafts for {price}, tax included.':
      'Szkice wliczone w abonament odnawiają się w ruchomym oknie 7 dni. Potrzebujesz więcej w tym samym tygodniu? Możesz kupić pakiet {count} dodatkowych szkiców za {price} z podatkiem.',
    'Your plan no longer covers every active client':
      'Twój abonament nie obejmuje już wszystkich aktywnych klientów',
    'You have {count} active clients on a plan for {limit}. Archive {overBy} to fit, or move to a larger plan. Archived clients keep their history and stop counting towards the limit.':
      'Masz {count} aktywnych klientów w abonamencie na {limit}. Zarchiwizuj {overBy}, aby się zmieścić, albo przejdź na większy abonament. Zarchiwizowani klienci zachowują historię i nie wliczają się do limitu.',
    'Archive clients': 'Zarchiwizuj klientów',
    'Update plan': 'Zmień abonament',
    'Opening billing portal...': 'Otwieram panel rozliczeń...',
    'For trainers coaching their first handful of clients':
      'Dla trenerów prowadzących pierwszych kilku klientów',
    'For trainers with a steady, growing client roster':
      'Dla trenerów ze stabilną, rosnącą bazą klientów',
    'For an established independent coaching practice':
      'Dla ugruntowanej, niezależnej praktyki trenerskiej',
    'Forma Plus': 'Forma Plus',
    'Manage your subscription before starting a trainer plan.':
      'Zarządzaj subskrypcją przed uruchomieniem abonamentu trenerskiego.',
    'Manage your existing subscription.': 'Zarządzaj obecną subskrypcją.',
    'This action is unavailable for your account.':
      'Ta czynność jest niedostępna na Twoim koncie.',
    'Archive a client before adding another client.':
      'Zarchiwizuj klienta przed dodaniem kolejnego.',

    'For people who want their own full routine workspace in Forma':
      'Dla osób, które chcą mieć własny pełny panel planów w Formie',
    'Unlock Forma Plus': 'Odblokuj Forma Plus',
    'Unlock with Forma Plus': 'Odblokuj z Forma Plus',
    'This routine is part of Forma Plus.': 'Ten plan należy do Forma Plus.',
    'Free routines': 'Bezpłatne plany',
    Access: 'Dostęp',
    'Unlock paid routines in the Forma library with Forma Plus.':
      'Odblokuj płatne plany w bibliotece Formy dzięki Forma Plus.',
    'Forma Plus subscriptions are not available just yet.':
      'Subskrypcje Forma Plus nie są jeszcze dostępne.',
    "You can keep using Forma, and upgrades will appear here when they're available.":
      'Możesz dalej korzystać z Formy, a ulepszenia pojawią się tutaj, gdy będą dostępne.',
    'Sign in to start Forma Plus.': 'Zaloguj się, aby włączyć Forma Plus.',
    'Forma Plus checkout is not available.':
      'Płatność za Forma Plus jest niedostępna.',
    'Forma Plus billing': 'Płatności Forma Plus',
    Subscription: 'Subskrypcja',
    'Manage your plan and billing details.':
      'Zarządzaj abonamentem i szczegółami płatności.',
    'Trainer plan': 'Abonament trenerski',
    'Forma plans': 'Abonamenty Forma',
    'Choose a Forma plan': 'Wybierz abonament Forma',
    'Choose trainer plan': 'Wybierz abonament trenerski',
    'Choose plan': 'Wybierz abonament',
    'Choose the right plan': 'Wybierz odpowiedni abonament',
    'Start with the active profile limit you need today and keep room to grow.':
      'Wybierz liczbę aktywnych profili dopasowaną do obecnego etapu pracy, z miejscem na spokojny rozwój.',
    'Active profile limit matched to the plan':
      'Limit aktywnych profili dopasowany do abonamentu',
    'Taxes shown before payment': 'Podatki pokazane przed płatnością',
    'Secure card processing': 'Bezpieczna obsługa karty',
    'Back to trainer home': 'Wróć do panelu trenera',
    'Choose a plan to start working with clients in Forma.':
      'Wybierz abonament, aby zacząć pracę z klientami w Formie.',
    'Pick the trainer plan, billing period, and currency before continuing to secure checkout.':
      'Wybierz abonament trenerski, okres rozliczeniowy i walutę przed przejściem do bezpiecznej płatności.',
    'Your trainer workspace is active.': 'Twój panel trenera jest aktywny.',
    'Update your payment details to keep trainer access available.':
      'Zaktualizuj dane płatności, aby utrzymać dostęp trenerski.',
    'Resolve billing': 'Rozwiąż problem z płatnością',
    '{plan} is active': 'Abonament {plan} jest aktywny',
    'Use the billing portal to update payment details, invoices, or your subscription.':
      'Użyj portalu płatności, aby zaktualizować dane płatności, faktury lub subskrypcję.',
    'Tax and invoices': 'Podatki i faktury',
    'Applicable tax is calculated during checkout from your billing country and tax status. Business customers can add tax details for invoices before payment.':
      'Należny podatek jest obliczany podczas płatności na podstawie kraju rozliczenia i statusu podatkowego. Klienci biznesowi mogą dodać dane podatkowe do faktury przed płatnością.',
    'Subscription terms': 'Warunki subskrypcji',
    'Plans renew automatically for the selected monthly or yearly period until cancelled. You can manage cancellation, payment details, and invoices through the secure billing portal.':
      'Abonamenty odnawiają się automatycznie w wybranym okresie miesięcznym lub rocznym do momentu anulowania. Anulowanie, dane płatności i faktury obsłużysz w bezpiecznym portalu płatności.',
    'Refunds and policies': 'Zwroty i zasady',
    'Refund and withdrawal rights are described in the Terms. Payment details are processed securely, and Forma does not store full card numbers or CVC codes.':
      'Zasady zwrotów i prawa odstąpienia opisują Warunki. Dane płatności są przetwarzane bezpiecznie, a Forma nie przechowuje pełnych numerów kart ani kodów CVC.',
    Terms: 'Warunki',
    Privacy: 'Prywatność',
    '{price} monthly equivalent': '{price} miesięcznie w przeliczeniu',
    '{price} net': '{price} netto',
    '{price} estimated gross at 23% VAT':
      '{price} brutto przy szacunkowej stawce VAT 23%',
    'Continue with {plan}': 'Kontynuuj z abonamentem {plan}',
    'Forma Plus billing service is not available.':
      'Obsługa płatności Forma Plus jest niedostępna.',
    'Update payment details, invoices, or your trainer subscription.':
      'Zaktualizuj dane płatności, faktury lub abonament trenerski.',
    'Manage trainer billing': 'Zarządzaj płatnościami abonamentu trenerskiego',
    'Update payment details, invoices, or your subscription.':
      'Zaktualizuj dane płatności, faktury lub subskrypcję.',
    'Manage Forma Plus': 'Zarządzaj Forma Plus',
    'Start Forma Plus': 'Rozpocznij Forma Plus',
    'Not now': 'Nie teraz',
    'Upgrade to Forma Plus to edit routines.':
      'Przejdź na Forma Plus, aby edytować plany.',
    'Billing service is not available.': 'Usługa płatności jest niedostępna.',
    'Manage billing': 'Zarządzaj subskrypcją',
    Upgrade: 'Zmień abonament',
    'Upgrade when you need more than one client.':
      'Zmień abonament, gdy potrzebujesz więcej niż jednego klienta.',
    'Trainer plan active': 'Abonament trenera aktywny',
    '{plan} plan · {clients} / {limit} clients':
      'Abonament {plan} · {clients} / {limit} klientów',
    '{clients} / {limit} clients': '{clients} / {limit} klientów',
    'Payment needs attention': 'Płatność wymaga uwagi',
    'Trainer plan canceled': 'Abonament trenera anulowany',
    'Trainer access inactive': 'Dostęp trenera nieaktywny',
    'Trainer access not requested': 'Dostęp trenera nie został zgłoszony',
    'Upgrade your Forma plan before editing routines.':
      'Zmień abonament Forma, aby edytować plany.',
    'Firebase is not configured yet. Add the web app env vars first.':
      'Firebase nie jest jeszcze skonfigurowany. Najpierw dodaj zmienne środowiskowe aplikacji webowej.',
    'Signed in successfully.': 'Zalogowano pomyślnie.',
    'Signed out.': 'Wylogowano.',
    'Add a routine name before creating it.':
      'Dodaj nazwę planu przed jego utworzeniem.',
    'Routine "{name}" created.': 'Plan „{name}” został utworzony.',
    'Select a routine before editing it.': 'Wybierz plan przed jego edycją.',
    'Routine name cannot be empty.': 'Nazwa planu nie może być pusta.',
    'Routine "{name}" updated.': 'Plan „{name}” został zaktualizowany.',
    'Delete "{name}"? This removes the routine and its exercises from your account.':
      'Usunąć „{name}”? Plan i jego ćwiczenia zostaną usunięte z Twojego konta.',
    'Sign in before adding routines.': 'Zaloguj się przed dodawaniem planów.',
    'Added "{name}" to your account.': 'Dodano „{name}” do Twojego konta.',
    'Select a routine before deleting it.':
      'Wybierz plan przed jego usunięciem.',
    'Routine "{name}" deleted.': 'Plan „{name}” został usunięty.',
    'Deleting routine...': 'Usuwanie planu...',
    'Routine actions': 'Akcje planu',
    'Create or select a routine first.': 'Najpierw utwórz lub wybierz plan.',
    'Add an exercise title first.': 'Najpierw dodaj tytuł ćwiczenia.',
    'Exercise added to {name}.': 'Ćwiczenie dodane do planu {name}.',
    'Reusable exercise added to {name}.':
      'Ćwiczenie do ponownego użycia dodane do planu {name}.',
    'Exercise storage is not available.':
      'Usługa przechowywania ćwiczeń jest niedostępna.',
    'Demo unavailable right now.': 'Demo jest teraz niedostępne.',
    'Loading demo...': 'Wczytywanie demo...',
    'Forma demo included': 'Demo Formy w zestawie',
    'This exercise already has a ready-to-play Forma demo.':
      'To ćwiczenie ma już gotowe do odtworzenia demo Formy.',
    'Forma is loading this demo.': 'Forma wczytuje to demo.',
    'This demo is attached but unavailable with the current plan.':
      'To demo jest dołączone, ale niedostępne w obecnym planie.',
    'This demo is attached but unavailable right now.':
      'To demo jest dołączone, ale teraz niedostępne.',
    'This exercise keeps its Forma demo.': 'To ćwiczenie zachowuje demo Formy.',
    'Use my own link': 'Użyj własnego linku',
    'Your Forma demo stays in place until you save a valid replacement.':
      'Demo Formy pozostanie bez zmian, dopóki nie zapiszesz prawidłowego zamiennika.',
    'Keep Forma demo': 'Zachowaj demo Formy',
    'This demo is not included in your plan.':
      'To demo nie jest dostępne w Twoim abonamencie.',
    'Exercise "{name}" saved.': 'Ćwiczenie „{name}” zostało zapisane.',
    'Select an exercise before editing it.':
      'Wybierz ćwiczenie przed jego edycją.',
    'Exercise title cannot be empty.': 'Tytuł ćwiczenia nie może być pusty.',
    'Exercise "{name}" updated.': 'Ćwiczenie „{name}” zostało zaktualizowane.',
    'Select a routine before deleting an exercise.':
      'Wybierz plan przed usunięciem ćwiczenia.',
    'Delete "{exercise}" from {routine}?':
      'Usunąć „{exercise}” z planu {routine}?',
    'Delete exercise': 'Usuń ćwiczenie',
    'Delete "{exercise}" from {routine}? This cannot be undone.':
      'Usunąć „{exercise}” z planu {routine}? Tej czynności nie można cofnąć.',
    'Deleting exercise...': 'Usuwanie ćwiczenia...',
    'Select an exercise before deleting it.':
      'Wybierz ćwiczenie przed jego usunięciem.',
    'Exercise "{name}" deleted.': 'Ćwiczenie „{name}” zostało usunięte.',
    'Sign in before updating reminders.':
      'Zaloguj się przed aktualizacją przypomnień.',
    'Reminder turned off.': 'Przypomnienie wyłączone.',
    'Reminder turned on.': 'Przypomnienie włączone.',
    'Forma member': 'Użytkownik Formy',
    Routines: 'Plany',
    'Choose a routine and manage its exercises.':
      'Wybierz plan i zarządzaj jego ćwiczeniami.',
    'Move up': 'Przenieś w górę',
    'Move down': 'Przenieś w dół',
    'Open actions for {title}': 'Otwórz akcje dla {title}',
    Settings: 'Ustawienia',
    English: 'Angielski',
    Polski: 'Polski',
    Reorder: 'Zmień kolejność',
    'Untitled exercise': 'Ćwiczenie bez tytułu',
    'Untitled routine': 'Plan bez tytułu',
    'Imported routine': 'Zaimportowany plan',
    Reminder: 'Przypomnienie',
    'Routine reminder': 'Przypomnienie o planie',
    scheduled: 'zaplanowano',
    'iPhone install': 'Instalacja na iPhonie',
    'Add Forma to your Home Screen from Safari.':
      'Dodaj Formę do ekranu głównego z Safari.',
    'Forma installs on iPhone through Safari.':
      'Na iPhonie Formę dodasz przez Safari.',
    'Open Safari actions and choose Share, View More, then Add to Home Screen.':
      'Otwórz akcje Safari i wybierz Udostępnij, Pokaż więcej, a potem Dodaj do ekranu głównego.',
    'This browser cannot add Forma to the Home Screen. Open this page in Safari, then follow the steps below.':
      'Ta przeglądarka nie może dodać Formy do ekranu głównego. Otwórz tę stronę w Safari, a potem wykonaj kroki poniżej.',
    'Step 1': 'Krok 1',
    Safari: 'Safari',
    More: 'Więcej',
    'Open Safari actions': 'Otwórz akcje Safari',
    'Tap the three dots in Safari to show page actions.':
      'Stuknij trzy kropki w Safari, aby pokazać akcje strony.',
    'Open the page in Safari, then tap the three dots.':
      'Otwórz stronę w Safari, a potem stuknij trzy kropki.',
    'Step 2': 'Krok 2',
    Share: 'Udostępnij',
    'Choose Share': 'Wybierz Udostępnij',
    'This opens the iPhone share sheet with the next install actions.':
      'Otworzy się panel udostępniania iPhone’a z kolejnymi akcjami instalacji.',
    'Step 3': 'Krok 3',
    'View More': 'Pokaż więcej',
    'Expand View More': 'Rozwiń Pokaż więcej',
    'If Add to Home Screen is hidden, expand the full actions list.':
      'Jeśli „Dodaj do ekranu głównego” jest ukryte, rozwiń pełną listę akcji.',
    'Step 4': 'Krok 4',
    Install: 'Zainstaluj',
    'Add to Home Screen': 'Dodaj do ekranu głównego',
    'Choose Add to Home Screen': 'Wybierz Dodaj do ekranu głównego',
    'Forma will open from your Home Screen like a regular app.':
      'Forma będzie otwierać się z ekranu głównego jak zwykła aplikacja.',
    'Copy link': 'Kopiuj link',
    'Maybe later': 'Może później',
    Language: 'Język',
    '{title} video thumbnail': 'Miniatura wideo: {title}',
    '{title} demo thumbnail': 'Miniatura demo: {title}',
    'Reduce remaining sets': 'Zmniejsz liczbę pozostałych serii',
    'Increase remaining sets': 'Zwiększ liczbę pozostałych serii',
    series: 'serie',
    reps: 'powtórzenia',
    time: 'czas',
    rest: 'przerwa',
    'Could not enable push notifications for this device.':
      'Nie udało się włączyć powiadomień push na tym urządzeniu.',
    'Firestore is not available for profile sync.':
      'Firestore nie jest dostępny do synchronizacji profilu.',
    'Danger zone': 'Strefa niebezpieczna',
    Account: 'Konto',
    'Manage your account and personal data.':
      'Zarządzaj swoim kontem i danymi osobowymi.',
    'Privacy Policy': 'Polityka prywatności',
    'Terms of Service': 'Regulamin',
    'Trainer DPA': 'Umowa powierzenia dla trenerów',
    'Data processing agreement': 'Umowa powierzenia danych',
    'Delete account': 'Usuń konto',
    'Permanently delete account?': 'Trwale usunąć konto?',
    'This permanently deletes your Forma profile, routines, activity, trainer relationships, and connected devices.':
      'Spowoduje to trwałe usunięcie profilu Forma, planów, aktywności, relacji trenerskich i połączonych urządzeń.',
    'Active subscriptions will be cancelled immediately. Deletion does not automatically issue a refund. Statutory rights and our refund policy still apply.':
      'Aktywne subskrypcje zostaną natychmiast anulowane. Usunięcie konta nie powoduje automatycznego zwrotu pieniędzy. Nadal obowiązują prawa ustawowe i nasza polityka zwrotów.',
    'Required billing, tax, dispute, fraud-prevention, and security records may be retained only as long as legally necessary.':
      'Wymagane dane rozliczeniowe, podatkowe, dotyczące sporów, zapobiegania oszustwom i bezpieczeństwa mogą być przechowywane wyłącznie tak długo, jak wymaga tego prawo.',
    'I understand this action is permanent and my application data cannot be recovered.':
      'Rozumiem, że ta czynność jest trwała i danych aplikacji nie będzie można odzyskać.',
    'Type DELETE to confirm': 'Wpisz DELETE, aby potwierdzić',
    'Confirm your password': 'Potwierdź hasło',
    'You may be asked to sign in again before deletion.':
      'Przed usunięciem konta może być wymagane ponowne logowanie.',
    'Sign in to request trainer access':
      'Zaloguj się, aby poprosić o dostęp trenerski',
    'Deleting account...': 'Usuwanie konta...',
    'Permanently delete account': 'Trwale usuń konto',
    'Account deletion cancelled because sign-in was not confirmed.':
      'Usuwanie konta anulowano, ponieważ logowanie nie zostało potwierdzone.',
    'Account could not be deleted. Nothing was removed. Please try again or contact support.':
      'Nie udało się usunąć konta. Niczego nie usunięto. Spróbuj ponownie lub skontaktuj się z pomocą.',
    'Your account and application data were deleted.':
      'Twoje konto i dane w aplikacji zostały usunięte.',

    // Trainer, watch, routine-editor and misc strings added in Polish backfill.
    'A new version of Forma is available.': 'Dostępna jest nowa wersja Formy.',
    Refresh: 'Odśwież',
    'Accept invite': 'Przyjmij zaproszenie',
    'Accept invite as {account}. Your personal routines stay private.':
      'Przyjmij zaproszenie jako {account}. Twoje prywatne plany pozostaną prywatne.',
    'Accept your Forma invite': 'Przyjmij zaproszenie do Formy',
    'Accepting invite...': 'Przyjmowanie zaproszenia...',
    'Add a routine before assigning one.': 'Dodaj plan, zanim go przypiszesz.',
    'Add a starter routine before assigning work.':
      'Dodaj plan startowy, zanim przypiszesz zadania.',
    'Add starter routine': 'Dodaj plan startowy',
    'Your client appears in the roster right away, so you can prepare routines before they join. Next you get a private link they use to verify.':
      'Klient od razu pojawi się na liście, więc możesz przygotować plany, zanim dołączy. Następnie otrzymasz prywatny link, którym klient się zweryfikuje.',
    'All available routines are already assigned to this client.':
      'Wszystkie dostępne plany są już przypisane do tego klienta.',
    'All routines': 'Wszystkie plany',
    'Remove a client or upgrade before adding another client.':
      'Usuń klienta lub przejdź na wyższy abonament, zanim dodasz kolejnego.',
    'Assign routine': 'Przypisz plan',
    'Assign another routine': 'Przypisz kolejny plan',
    Assigned: 'Przypisane',
    'Assigned by trainer': 'Przypisane przez trenera',
    'Assigned routines': 'Przypisane plany',
    'Assigned to 1 client': 'Przypisane do 1 klienta',
    'Assigned to {count} clients': 'Przypisane do {count} klientów',
    'Browse starter routines': 'Przeglądaj plany startowe',
    'Choose one routine to assign to this client.':
      'Wybierz jeden plan do przypisania temu klientowi.',
    'Client could not be found.': 'Nie znaleziono klienta.',
    'Client name': 'Nazwa klienta',
    'Client not found': 'Nie znaleziono klienta',
    Clients: 'Klienci',
    'Clients and routines': 'Klienci i plany',
    'Connect Apple Watch': 'Połącz Apple Watch',
    'Connect this guest account before accepting trainer assignments. Your existing routines stay private.':
      'Połącz to konto gościa, zanim przyjmiesz przypisania od trenera. Twoje dotychczasowe plany pozostaną prywatne.',
    'Connecting your watch...': 'Łączenie z zegarkiem...',
    'Copied to clipboard.': 'Skopiowano do schowka.',
    'Add your first client to get their private invite link. You can assign routines right away — they appear when your client verifies.':
      'Dodaj pierwszego klienta, aby otrzymać jego prywatny link z zaproszeniem. Plany możesz przypisywać od razu — pojawią się, gdy klient się zweryfikuje.',
    'Create a new routine from scratch, or start from an existing Forma routine.':
      'Utwórz nowy plan od zera albo zacznij od istniejącego planu Formy.',
    'Create a routine or reuse one from your library.':
      'Utwórz plan albo użyj jednego ze swojej biblioteki.',
    'Create from scratch': 'Utwórz od zera',
    'Create routine from scratch': 'Utwórz plan od zera',
    'Create your Forma professional account':
      'Utwórz konto specjalisty w Formie',
    'Customize routine copy': 'Dostosuj kopię planu',
    Duplicate: 'Duplikuj',
    'Duplicated "{name}".': 'Zduplikowano „{name}”.',
    'Each rep follows the tempo phases. A {seconds}s rest runs between remaining sets.':
      'Każde powtórzenie przebiega według faz tempa. Między pozostałymi seriami trwa {seconds}s przerwy.',
    'Firestore denied the device registration. Deploy the latest Firestore rules first.':
      'Firestore odrzucił rejestrację urządzenia. Najpierw wdróż najnowsze reguły Firestore.',
    'Filter routines': 'Filtruj plany',
    'Forma can now send routines to your watch.':
      'Forma może teraz wysyłać plany na Twój zegarek.',
    'Forma is a simple app for following routines your trainer assigns between sessions.':
      'Forma to prosta aplikacja do wykonywania planów, które trener przypisuje między sesjami.',
    'Get started': 'Zacznij',
    'Give this copy a clear name before assigning it.':
      'Nadaj tej kopii czytelną nazwę, zanim ją przypiszesz.',
    'Choose a routine and set its schedule.':
      'Wybierz plan i ustaw jego harmonogram.',
    'Start a new routine or reuse one from your library.':
      'Utwórz nowy plan albo użyj jednego ze swojej biblioteki.',
    'How Forma works': 'Jak działa Forma',
    'Import a starter routine before assigning one.':
      'Zaimportuj plan startowy, zanim go przypiszesz.',
    'Invite accepted': 'Zaproszenie przyjęte',
    'Invite already used': 'Zaproszenie już wykorzystane',
    'Add clients, then assign routines from your library.':
      'Dodaj klientów, a następnie przypisz plany z Twojej biblioteki.',
    'Add one client to test the trainer routine loop.':
      'Dodaj jednego klienta, aby przetestować przepływ przypisywania planów.',
    "Review {name}'s week and keep their next routine moving.":
      'Sprawdź tydzień klienta {name} i zadbaj o kolejny krok.',
    'Review missed routine': 'Sprawdź pominięty plan',
    'Review due routine': 'Sprawdź plan na dziś',
    'Open client': 'Otwórz klienta',
    '{action} for {name}': '{action}: {name}',
    '{plan} plan': 'Plan {plan}',
    'Invited by your trainer': 'Zaproszenie od twojego trenera',
    'Invited by {trainer}': 'Zaproszenie od: {trainer}',
    'Keep exploring': 'Odkrywaj dalej',
    'Last done yesterday': 'Ostatnio wykonano wczoraj',
    'Last done {count} days ago': 'Ostatnio wykonano {count} dni temu',
    'Latest invite link': 'Najnowszy link z zaproszeniem',
    'Loading assignments...': 'Ładowanie przypisań...',
    'Loading clients...': 'Ładowanie klientów...',
    'Loading exercises...': 'Ładowanie ćwiczeń...',
    'Loading routine...': 'Ładowanie planu...',
    'Loading trainer workspace...': 'Ładowanie panelu trenera...',
    'Name a blank routine, then add exercises in the editor.':
      'Nazwij pusty plan, a potem dodaj ćwiczenia w edytorze.',
    'Name a new routine, then add exercises before assigning.':
      'Nazwij nowy plan, a potem dodaj ćwiczenia przed przypisaniem.',
    'No clients yet': 'Brak klientów',
    'No payment required. Sign in once to keep your routines attached to your account.':
      'Bez płatności. Zaloguj się raz, aby powiązać plany ze swoim kontem.',
    'No routine yet': 'Brak planu treningowego',
    'No routines assigned yet.': 'Nie przypisano jeszcze żadnych planów.',
    'No starter routines are available yet.':
      'Nie ma jeszcze dostępnych planów startowych.',
    'No trainer routines yet. Add one to unlock assignment.':
      'Brak planów trenerskich. Dodaj jeden, aby odblokować przypisywanie.',
    'No unassigned routines match that search.':
      'Żaden nieprzypisany plan nie pasuje do wyszukiwania.',
    'Not started': 'Nie rozpoczęto',
    'Not the right account? Sign out': 'Niewłaściwe konto? Wyloguj się',
    'Open my routines': 'Otwórz moje plany',
    'Open {name}': 'Otwórz {name}',
    'Preview {name}': 'Podgląd {name}',
    'Pick existing routine': 'Wybierz istniejący plan',
    'Use one of your trainer routines and set its schedule.':
      'Użyj jednego ze swoich planów trenerskich i ustaw jego harmonogram.',
    'Preview a starter routine and add a private copy.':
      'Podejrzyj plan startowy i dodaj prywatną kopię.',
    'Preview a starter routine, then add a private copy to assign to clients.':
      'Podejrzyj plan startowy, a następnie dodaj prywatną kopię do przypisania klientom.',
    'Push setup failed: {code}':
      'Konfiguracja powiadomień nie powiodła się: {code}',
    'Push setup is incomplete on this device. Try reopening the installed app and enabling notifications again.':
      'Konfiguracja powiadomień na tym urządzeniu nie została dokończona. Spróbuj ponownie otworzyć zainstalowaną aplikację i włączyć powiadomienia.',
    'Ready to connect your watch.': 'Możesz połączyć zegarek.',
    Remove: 'Usuń',
    'Remove "{name}" from your routines?': 'Usunąć „{name}” z Twoich planów?',
    'Rename client': 'Zmień nazwę klienta',
    'Notes ({count})': 'Notatki ({count})',
    'Notes · {name}': 'Notatki · {name}',
    'Only you can see these.': 'Widoczne tylko dla Ciebie.',
    'Add a note': 'Dodaj notatkę',
    'What did you notice in this session?': 'Co zauważyłeś na tym treningu?',
    'Save note': 'Zapisz notatkę',
    'Saving note...': 'Zapisywanie notatki...',
    'Loading notes...': 'Wczytywanie notatek...',
    'No notes yet. Anything you jot down stays here.':
      'Nie masz jeszcze notatek. Wszystko, co tu zapiszesz, zostaje.',
    'Edit note': 'Edytuj notatkę',
    'Delete this note?': 'Usunąć tę notatkę?',
    'Note deleted.': 'Notatka usunięta.',
    'Notes are not available right now.': 'Notatki są teraz niedostępne.',
    'Just now': 'Właśnie teraz',
    'Renamed client to {name}.': 'Zmieniono nazwę klienta na {name}.',
    'Routine "{name}" removed.': 'Plan „{name}” usunięty.',
    'Routine assigned to {client}.': 'Plan przypisany do {client}.',
    'Routine copy service is not available.':
      'Usługa kopiowania planów jest niedostępna.',
    'Routine creation service is not available.':
      'Usługa tworzenia planów jest niedostępna.',
    'Routine editor': 'Edytor planu',
    'Routine import service is not available.':
      'Usługa importu planów jest niedostępna.',
    'Routine not found': 'Nie znaleziono planu',
    'Routine overview': 'Przegląd planu',
    'Routine storage is not available.':
      'Usługa przechowywania planów jest niedostępna.',
    'Routine unassigned from {client}.': 'Plan odpięty od {client}.',
    'Sample profiles and routines. Nothing here is saved — create an account to start your own workspace.':
      'Przykładowe profile i plany. Niczego tu nie zapisujemy — utwórz konto, aby rozpocząć pracę we własnym panelu.',
    Save: 'Zapisz',
    'Save name': 'Zapisz nazwę',
    'Saving name...': 'Zapisywanie nazwy...',
    'Saving will create one reminder for each schedule time.':
      'Po zapisaniu powstanie po jednym przypomnieniu dla każdej zaplanowanej godziny.',
    'Search by goal, area, or routine name':
      'Szukaj według celu, obszaru lub nazwy planu',
    'Search by routine name': 'Szukaj według nazwy planu',
    'Search by routine name or note': 'Szukaj według nazwy planu lub notatki',
    'Search your routines, then choose one to schedule for this client.':
      'Przeszukaj swoje plany, a potem wybierz jeden do zaplanowania dla tego klienta.',
    'Clear the search or create a routine from scratch.':
      'Wyczyść wyszukiwanie albo utwórz plan od zera.',
    'Search routines': 'Szukaj planów',
    'Select the link and copy it manually.':
      'Zaznacz link i skopiuj go ręcznie.',
    'Set up the routine shell now, then add exercises next.':
      'Teraz ustaw podstawy planu, a potem dodaj ćwiczenia.',
    'Sign in before accepting a trainer invite.':
      'Zaloguj się, zanim przyjmiesz zaproszenie od trenera.',
    'Sign in before pairing your watch.':
      'Zaloguj się, zanim sparujesz zegarek.',
    'Sign in to Forma to finish pairing this watch.':
      'Zaloguj się do Formy, aby dokończyć parowanie tego zegarka.',
    'Sign in to continue to Checkout.':
      'Zaloguj się, aby przejść do płatności.',
    Source: 'Źródło',
    'Started today': 'Rozpoczęto dzisiaj',
    'Started yesterday': 'Rozpoczęto wczoraj',
    'Started {count} days ago': 'Rozpoczęto {count} dni temu',
    'Starter routines could not load right now.':
      'Nie udało się załadować planów startowych.',
    "Trainer's only": 'Tylko trenera',
    'Stripe Billing Portal did not return a link.':
      'Portal rozliczeniowy Stripe nie zwrócił linku.',
    'Checkout did not return a payment link.':
      'Nie udało się utworzyć linku do płatności.',
    'That watch QR expired. Refresh it on your watch and scan again.':
      'Kod QR zegarka wygasł. Odśwież go na zegarku i zeskanuj ponownie.',
    'That watch QR has already been used. Refresh it on your watch.':
      'Ten kod QR zegarka został już użyty. Odśwież go na zegarku.',
    'That watch pairing link is not valid anymore.':
      'Ten link do parowania zegarka nie jest już ważny.',
    'The push registration service is temporarily unavailable.':
      'Usługa rejestracji powiadomień jest tymczasowo niedostępna.',
    'This client already has {count} active routines. Keep assignments focused.':
      'Ten klient ma już {count} aktywnych planów. Zadbaj o to, by przypisania były przemyślane.',
    'This client connection is no longer available.':
      'To połączenie z klientem nie jest już dostępne.',
    'This invite link has already been used.':
      'Ten link z zaproszeniem został już wykorzystany.',
    'This invite link has already been used. Ask your trainer for a new link.':
      'Ten link z zaproszeniem został już wykorzystany. Poproś trenera o nowy link.',
    'This is a live demo, so editing is turned off. Create your own account to invite clients, build routines, and assign programs.':
      'To wersja demo, dlatego edycja jest wyłączona. Utwórz własne konto, aby zapraszać klientów, tworzyć plany i przypisywać je klientom.',
    'This is your trainer invite': 'To jest Twoje zaproszenie od trenera',
    'This only changes how the client appears in your trainer workspace. It does not change their Forma account.':
      'To zmienia tylko sposób wyświetlania klienta w Twoim panelu trenera. Nie zmienia jego konta w Formie.',
    'This routine is no longer available.': 'Ten plan nie jest już dostępny.',
    'Trainer access is inactive': 'Dostęp trenera jest nieaktywny',
    'Free access until {date}': 'Bezpłatny dostęp do {date}',
    'Free access ends on {date}': 'Bezpłatny dostęp kończy się {date}',
    'Free access ended on {date}': 'Bezpłatny dostęp zakończył się {date}',
    'Write to us if you need more time.':
      'Napisz do nas, jeśli potrzebujesz więcej czasu.',
    'If you subscribe now, your first payment is after that date, so you keep your free days.':
      'Jeśli wykupisz abonament teraz, pierwsza płatność będzie dopiero po tym dniu, więc nie stracisz bezpłatnych dni.',
    'If you subscribe now, payment starts right away.':
      'Jeśli wykupisz abonament teraz, płatność zacznie się od razu.',
    'See subscription': 'Sprawdź abonament',
    'Forma trainer access': 'Dostęp do Formy dla trenerów',
    'I would like to keep using the Forma trainer workspace.':
      'Chcę dalej korzystać z panelu trenera w Formie.',
    'Your clients and routines are saved. Assigned routines are paused until access is restored.':
      'Klienci i plany są zachowane. Przypisane plany są wstrzymane do czasu przywrócenia dostępu.',
    'Trainer assignment service is not available.':
      'Usługa przypisań trenera jest niedostępna.',
    'Trainer billing service is not available.':
      'Usługa rozliczeń trenera jest niedostępna.',
    'Trainer invite accepted.': 'Zaproszenie od trenera przyjęte.',
    'Trainer invite could not be accepted.':
      'Nie udało się przyjąć zaproszenia od trenera.',
    'Trainer invite link is invalid.':
      'Link z zaproszeniem od trenera jest nieprawidłowy.',
    'Trainer invite service error. Please try again.':
      'Błąd usługi zaproszeń trenera. Spróbuj ponownie.',
    'Trainer invite service is not available.':
      'Usługa zaproszeń trenera jest niedostępna.',
    'Trainer invite service is temporarily unavailable.':
      'Usługa zaproszeń trenera jest tymczasowo niedostępna.',
    'Trainer invite was not found.': 'Nie znaleziono zaproszenia od trenera.',
    'Trainer workspace': 'Panel trenera',
    'Review what needs your attention today.':
      'Sprawdź, co wymaga Twojej uwagi dzisiaj.',
    'Exercise plan workspace': 'Panel planów ćwiczeń',
    'Create exercise plans, assign them, and review completion.':
      'Twórz plany ćwiczeń, przypisuj je i sprawdzaj realizację.',
    'Open trainer workspace': 'Otwórz panel trenera',
    Unassign: 'Odepnij',
    'Unassign "{routine}" from {client}?': 'Odpiąć „{routine}” od {client}?',
    'Unassign this routine from clients before removing it.':
      'Odepnij ten plan od klientów, zanim go usuniesz.',
    'Watch pairing failed. Refresh the QR on your watch and try again.':
      'Parowanie zegarka nie powiodło się. Odśwież kod QR na zegarku i spróbuj ponownie.',
    'Watch pairing failed: {code}':
      'Parowanie zegarka nie powiodło się: {code}',
    'Watch pairing is temporarily unavailable.':
      'Parowanie zegarka jest tymczasowo niedostępne.',
    'You are already connected to this trainer.':
      'Masz już połączenie z tym trenerem.',
    'You cannot accept your own trainer invite.':
      'Nie możesz przyjąć własnego zaproszenia trenera.',
    'You cannot accept your own trainer invite. Share this link with a client instead.':
      'Nie możesz przyjąć własnego zaproszenia trenera. Udostępnij ten link klientowi.',
    'Your personal routines are still available. Trainer invites and assignments are paused until access is restored.':
      'Twoje prywatne plany są nadal dostępne. Zaproszenia i przypisania trenerskie są wstrzymane do czasu przywrócenia dostępu.',
    'Your trainer connection is ready. Routines your trainer prepared are waiting in Forma.':
      'Połączenie z trenerem jest gotowe. Plany przygotowane przez trenera czekają w Formie.',
    'This invitation has already connected your client. You can keep coaching them from your trainer workspace.':
      'To zaproszenie połączyło już klienta. Możesz dalej prowadzić go w panelu trenera.',
    'Invited by {trainer} as {name}': 'Zaproszenie od: {trainer} dla: {name}',
    'Awaiting verification': 'Oczekuje na weryfikację',
    'Prepare client': 'Przygotuj klienta',
    'Routines are ready — share the invite link so they can start.':
      'Plany są gotowe — udostępnij link z zaproszeniem, aby klient mógł zacząć.',
    'Share the invite link, then prepare routines for their start.':
      'Udostępnij link z zaproszeniem i przygotuj plany na początek pracy klienta.',
    '{name} hasn’t verified yet': '{name} jeszcze nie zweryfikował(a) konta',
    'Routines you assign now are waiting for them — share the invite link so they can connect.':
      'Plany, które przypiszesz teraz, będą już czekać — udostępnij link z zaproszeniem, aby klient mógł się połączyć.',
    'Share invite link': 'Udostępnij link z zaproszeniem',
    'Your watch is connected. Return to the watch to continue.':
      'Twój zegarek jest połączony. Wróć do zegarka, aby kontynuować.',
    'You’re already connected': 'Masz już połączenie',
    clients: 'klientów',
    'e.g. Alex Morgan': 'np. Anna Kowalska',
    'this account': 'to konto',
    'your trainer': 'Twój trener',
    '{count} assigned': 'przypisano: {count}',
    '{current} / {total}': '{current} / {total}',
    'Privacy choices': 'Wybory prywatności',
    'Help us improve Forma': 'Pomóż nam ulepszać Formę',
    'We use privacy-friendly analytics and crash reporting to keep Forma fast and stable. Nothing loads until you choose.':
      'Używamy analityki przyjaznej prywatności i raportowania błędów, aby Forma działała szybko i stabilnie. Niczego nie uruchomimy, dopóki nie zdecydujesz.',
    Reject: 'Odrzuć',
    Accept: 'Akceptuję',
  },
};

function fillTemplate(message: string, variables: TranslationVariables = {}) {
  return Object.entries(variables).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
    message,
  );
}

export function createTranslator(language: AppLanguage): Translator {
  return Object.assign(
    (message: string, variables?: TranslationVariables) => {
      const template = translations[language][message] ?? message;
      return fillTemplate(template, variables);
    },
    { language },
  );
}

/** The set of message keys that have an explicit entry for a language. */
export function getTranslationKeys(language: AppLanguage): readonly string[] {
  return Object.keys(translations[language]);
}
